import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  DialogTitle,
  Autocomplete,FormControlLabel, Checkbox
} from "@mui/material";
import { useFormik } from "formik";
import {
  useCreateCustomerMutation,
  useCreateWorkOrderMutation,
  useEditInvoiceMutation,
  useGetAllSearchCompanyQuery,
  useGetCompanyQuery,
  useGetCustomerQuery,
  useGetLastWorkOrderIdQuery,
  useGetPriceHistoryQuery,
  useGetProductQuery,
  useGetTaxQuery,
  useGetWorkOrderQuery,
  useGetTripsByCustomerQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import {
  ICompanyDetails,
  ICustomer,
  IPriceHistoryPayload,
  IPreviousTripItem,
  IProductPopUP,
  ITaxes,
} from "../../../Api/Interface/api.interface";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toDate } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { normalizeDateTimeFields } from "../../../Core/utils/dateTime";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import AddIcon from "@mui/icons-material/Add";
import { FaTrash } from "react-icons/fa";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import NewCompanyPopUp from "../../CompanyInfo/Components/NewCompanyPopUp";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker
} from "@mui/x-date-pickers";
import DateTimeDropdownPicker from "../../../Components/DateTimeDropdownPicker";
import PriceSuggestionCard from "../../../Components/PriceSuggestionCard";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { FaEdit } from "react-icons/fa";
import placeholderImage from '../../../Images/fav.png';
import { City } from "country-state-city";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DeleteIcon from "@mui/icons-material/Delete";

const generateWorkOrderNumber = (workOrderId: number) => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `WO${year}${month}${day}-${workOrderId.toString().padStart(2, "0")}`;
};

interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  busCount: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  price: number | null;
  tax: number;
  taxAmount: number;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
   pickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null; description?: string }[];
    dropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null; description?: string }[];
    returnPickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null; description?: string }[];
    returnDropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null; description?: string }[];
    useSameAsOutbound?: boolean;
}

interface IDiscount {
  value: number | null;
  type?: "fixed" | "percent";
  description: string;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  isInvoice: boolean;
  invoiceId?: number | null;
  InvoiceNo?: string | null;
  po_number?: string | null;
  invoice?: { id: number; InvoiceNo?: string | null; po_number?: string | null } | null;
}

interface IProductSub {
  productName: string;
  transferTypeTo: string;
  transferTypeFrom: string;
}

const quoteInfoLabel = {
  fontSize: 10,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase" as const,
  letterSpacing: 0.6,
  mb: 0.25,
};

const quoteInfoValue = {
  fontSize: 13,
  fontWeight: 500,
  color: "#1e293b",
  lineHeight: 1.5,
};

const QuoteInfoBlock = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <Typography sx={quoteInfoLabel}>{label}</Typography>
    <Typography sx={quoteInfoValue}>{value || "—"}</Typography>
  </Box>
);

const datePickerSx = {
  "& .MuiInputBase-root": { bgcolor: "#ffffff" },
  "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
};

interface NominatimResult {
  display_name: string;
  address: { house_number?: string; road?: string; city?: string; town?: string; village?: string; county?: string; };
}

// ── Accent-insensitive city helpers ──────────────────────────────────────────
const normStr = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const cityFilterOptions = (options: string[], { inputValue }: { inputValue: string }) => {
  if (!inputValue) return options;
  const n = normStr(inputValue);
  return options.filter((o) => normStr(o).includes(n));
};
// ─────────────────────────────────────────────────────────────────────────────

interface ISavedLocation {
  description: string;
  address: string;
  city: string;
  usedCount: number;
  lastUsedAt: string;
}

const LOCATION_STORAGE_KEY = "tjbus_saved_locations";

const getSavedLocations = (): ISavedLocation[] => {
  try { return JSON.parse(localStorage.getItem(LOCATION_STORAGE_KEY) || "[]"); }
  catch { return []; }
};

const persistLocation = (description: string, address: string, city: string) => {
  if (!address.trim()) return;
  const locations = getSavedLocations();
  const nk = (s: string) => s.trim().toLowerCase();
  const idx = locations.findIndex(l => nk(l.address) === nk(address) && nk(l.city) === nk(city));
  if (idx >= 0) {
    locations[idx].usedCount++;
    locations[idx].lastUsedAt = new Date().toISOString();
    if (description.trim()) locations[idx].description = description.trim();
  } else {
    locations.push({ description: description.trim(), address: address.trim(), city: city.trim(), usedCount: 1, lastUsedAt: new Date().toISOString() });
  }
  locations.sort((a, b) => b.usedCount - a.usedCount || (b.lastUsedAt > a.lastUsedAt ? 1 : -1));
  localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locations.slice(0, 100)));
};

const AddressAutocomplete = ({
  value, onChange, onSelect, placeholder = "Street address", savedLocations = [],
}: {
  value: string; onChange: (val: string) => void; onSelect: (address: string, city: string, description?: string) => void; placeholder?: string; savedLocations?: ISavedLocation[];
}) => {
  const [nominatimResults, setNominatimResults] = React.useState<Array<{ addr: string; city: string; display: string }>>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredSaved = React.useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return savedLocations.slice(0, 5);
    return savedLocations.filter(l =>
      l.address.toLowerCase().includes(q) || l.description.toLowerCase().includes(q)
    ).slice(0, 5);
  }, [value, savedLocations]);

  const search = (val: string) => {
    onChange(val);
    if (timer.current) clearTimeout(timer.current);
    const q = val.trim();
    if (q.length < 4) { setNominatimResults([]); setOpen(filteredSaved.length > 0); return; }
    setOpen(true);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=ca&addressdetails=1&limit=5`;
        const res = await fetch(url, { headers: { "Accept-Language": "en" } });
        const data: NominatimResult[] = await res.json();
        setNominatimResults(data.map(item => ({
          addr: [item.address?.house_number, item.address?.road].filter(Boolean).join(" ") || item.display_name.split(",")[0],
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.county || "",
          display: item.display_name,
        })));
      } catch { setNominatimResults([]); } finally { setLoading(false); }
    }, 450);
  };

  const hasItems = filteredSaved.length > 0 || nominatimResults.length > 0;

  return (
    <Box sx={{ position: "relative" }}>
      <TextField size="small" fullWidth placeholder={placeholder} value={value}
        onChange={(e) => search(e.target.value)}
        onFocus={() => { if (hasItems) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        InputProps={{ startAdornment: loading ? <InputAdornment position="start"><CircularProgress size={12} /></InputAdornment> : undefined, sx: { bgcolor: "#ffffff" } }} />
      {open && hasItems && (
        <Paper sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", mt: 0.5 }}>
          {filteredSaved.length > 0 && (
            <>
              <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Recent Locations</Typography>
              </Box>
              {filteredSaved.map((loc, i) => (
                <MenuItem key={`saved-${i}`} onClick={() => { onSelect(loc.address, loc.city, loc.description); setOpen(false); }} sx={{ py: 0.75, "&:hover": { bgcolor: "#eff6ff" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#2563eb", flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }} noWrap>{loc.address}{loc.city ? `, ${loc.city}` : ""}</Typography>
                      {loc.description && <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{loc.description}</Typography>}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>×{loc.usedCount}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </>
          )}
          {nominatimResults.length > 0 && (
            <>
              {filteredSaved.length > 0 && <Box sx={{ borderTop: "1px solid #e2e8f0" }} />}
              <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Address Search</Typography>
              </Box>
              {nominatimResults.map((s, i) => (
                <MenuItem key={`nom-${i}`} onClick={() => { onSelect(s.addr, s.city); setOpen(false); }} sx={{ py: 0.75, "&:hover": { bgcolor: "#eff6ff" } }}>
                  <Box><Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{s.addr}</Typography><Typography sx={{ fontSize: 11, color: "#64748b" }}>{s.display}</Typography></Box>
                </MenuItem>
              ))}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

const Index: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: QuoteId } = useParams<{ id: string }>();
  useEffect(() => {}, [QuoteId]);

  const {
    data: workOrderData,
    isLoading: workIsLoading,
    isError: workOrderError,
    refetch: workOrderRefetch,
  } = useGetWorkOrderQuery({
    searchText: "",
    id: String(QuoteId),
  });

  const WorkOrderDataList = useMemo(() => {
    if (
      workOrderData &&
      Array.isArray(workOrderData.data) &&
      workOrderData.data.length > 0
    ) {
      return workOrderData.data[0] as IQuoteWorkOrder;
    }
    return null;
  }, [workOrderData]);

  const {
      data: companyData,  isLoading: companyIsLoading, isError,refetch,} = useGetCompanyQuery({
      searchText: "",
      id: "",
    });

  useEffect(() => {
    if (
      workOrderData &&
      Array.isArray(workOrderData.data) &&
      workOrderData.data.length > 0
    ) {
      const workOrder = workOrderData.data[0];
      // Once the work order has been converted to an invoice, the invoice owns
      // the live sale rows (including any pickup/dropoff dateTime edits).
      const invoice: any = (workOrder as any)?.invoice;
      const invoiceSale = Array.isArray(invoice?.quoteWorkOrderSale)
        ? invoice.quoteWorkOrderSale
        : (typeof invoice?.quoteWorkOrderSale === "string"
            ? (() => { try { return JSON.parse(invoice.quoteWorkOrderSale); } catch { return null; } })()
            : null);
      const saleProducts = (invoiceSale && invoiceSale.length > 0)
        ? invoiceSale
        : workOrder.quoteWorkOrderSale;

      const mappedProductRows = saleProducts.map((saleProduct: IProduct) => ({
        productName: saleProduct.productName,
        productDescription: saleProduct.productDescription,
        productImg: saleProduct.productImg,
        numberOfPassengers: saleProduct.numberOfPassengers,
        busCount: saleProduct.busCount,
        transferType: saleProduct.transferType,
        transferTypeFrom: saleProduct.transferTypeFrom,
        transferTypeTo: saleProduct.transferTypeTo,
        transferTypeDescription: saleProduct.transferTypeDescription || "",
        departureDateTime: dayjs(saleProduct?.departureDateTime) || null,
        returnDateTime: dayjs(saleProduct?.returnDateTime) || null,
        departureAddress: saleProduct.departureAddress || "",
        destinationAddress: saleProduct.destinationAddress || "",
        multiDestination: saleProduct.multiDestination || "",
        quantity: saleProduct.quantity,
        price: saleProduct.price,
        tax: saleProduct.tax || 0,
        taxAmount: saleProduct.taxAmount || 0,
        taxId: saleProduct.taxId || null,
        total: saleProduct.total,
        unitPriceWithDiscount: saleProduct.unitPriceWithDiscount,
        unitTotalWithDiscount: saleProduct.unitTotalWithDiscount,
        productType: saleProduct.productType,
        productLongDescription: saleProduct.productLongDescription || "",
        productShortDescription: saleProduct.productShortDescription || "",
        
        useSameAsOutbound: saleProduct.useSameAsOutbound ?? false,

         pickups:
    saleProduct.pickups?.map((p) => ({
      address: p.address || "",
      city: p.city || "",
      dateTime: p.dateTime ? dayjs(p.dateTime) : null,
      description: p.description || "",
    })) || [],

  dropoffs:
    saleProduct.dropoffs?.map((d) => ({
      address: d.address || "",
      city: d.city || "",
      dateTime: d.dateTime ? dayjs(d.dateTime) : null,
      description: d.description || "",
    })) || [],

  returnPickups:
    saleProduct.returnPickups?.map((p) => ({
      address: p.address || "",
      city: p.city || "",
      dateTime: p.dateTime ? dayjs(p.dateTime) : null,
      description: p.description || "",
    })) || [],

  returnDropoffs:
    saleProduct.returnDropoffs?.map((d) => ({
      address: d.address || "",
      city: d.city || "",
      dateTime: d.dateTime ? dayjs(d.dateTime) : null,
      description: d.description || "",
    })) || [],
      }));
      

      setProductRows(mappedProductRows);
    }
  }, [workOrderData]);

  useEffect(() => {
      // Fetch all cities in Canada (country code "CA")
      const cities = City.getCitiesOfCountry("CA");
      if (cities) {
        setCanadianCities(Array.from(new Set(cities.map((city) => city.name))).sort());
      }
    }, []);

  useEffect(() => {
    if (
      workOrderData &&
      Array.isArray(workOrderData.data) &&
      workOrderData.data.length > 0
    ) {
      const workOrder = workOrderData.data[0];
      const invoice: any = (workOrder as any)?.invoice;
      const invoiceDiscDesc = Array.isArray(invoice?.quoteWorkOrderSaleDiscountDescription)
        ? invoice.quoteWorkOrderSaleDiscountDescription
        : (typeof invoice?.quoteWorkOrderSaleDiscountDescription === "string"
            ? (() => { try { return JSON.parse(invoice.quoteWorkOrderSaleDiscountDescription); } catch { return null; } })()
            : null);
      const saleProducts = (invoiceDiscDesc && invoiceDiscDesc.length > 0)
        ? invoiceDiscDesc
        : workOrder.quoteWorkOrderSaleDiscountDescription;

      const mappedProductRows = saleProducts.map((saleProduct: IDiscount) => ({
        value: saleProduct.value,
        description: saleProduct.description,
      }));

      setDiscounts(mappedProductRows);
    }
  }, [workOrderData]);

  const [newTax, { isLoading }] = useCreateWorkOrderMutation();
  const [editInvoiceMutation, { isLoading: isInvoiceSaving }] = useEditInvoiceMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const invoiceRecordId = useMemo(() => {
    const wo: any = WorkOrderDataList;
    return wo?.invoice?.id ?? wo?.invoiceId ?? null;
  }, [WorkOrderDataList]);
  const hasInvoice = !!invoiceRecordId;
  const displayInvoiceNo = (WorkOrderDataList as any)?.InvoiceNo || (WorkOrderDataList as any)?.invoice?.InvoiceNo || "";
  const displayPoNumber = (WorkOrderDataList as any)?.po_number || (WorkOrderDataList as any)?.invoice?.po_number || "";

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const [searchQueryCustomer, setSearchQueryCustomer] = useState<string>("");
  const [currentSearchQueryCustomer, setCurrentSearchQueryCustomer] =
    useState<string>("");
  const debouncedSearchQueryCustomer = useDebounce(searchQueryCustomer, 300);
  const [showDropdownCustomer, setShowDropdownCustomer] =
    useState<boolean>(false);

  const [searchQueryProduct, setSearchQueryProduct] = useState<string>("");
  const [currentSearchQueryProduct, setCurrentSearchQueryProduct] =
    useState<string>("");
  const debouncedSearchQueryProduct = useDebounce(searchQueryProduct, 300);
  const [showDropdownProductIndex, setShowDropdownProductIndex] = useState<
    number | null
  >(null);

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const [averagePrice, setAveragePrice] = useState<number | null>(null);
  const [nextPrice, setNextPrice] = useState<number | null>(null);
  const [quoteWorkOrderNoForPrice, setQuoteWorkOrderNoForPrice] =
    useState<String | null>(null);
  const [updated_at, setUpdated_at] = useState<Date | null>(null);
  const [priceId, setPriceId] = useState<number | null>(null);

  const [currentPriceQueryParams, setCurrentPriceQueryParams] =
    useState<IPriceHistoryPayload | null>(null);

  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
     const [popupOpen, setPopupOpen] = useState(false);
      const [editIndex, setEditIndex] = useState<number | null>(null);
      const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
      const [originalProduct, setOriginalProduct] = useState<IProduct | null>(null); 
      const [canadianCities, setCanadianCities] = useState<string[]>([]);
      const matchCity = (city: string): string => {
        if (!city) return city;
        const n = normStr(city);
        return canadianCities.find((c) => normStr(c) === n) || city;
      };
      const [errors, setErrors] = useState<{ departureDateTime?: string; returnDateTime?: string; productName?: string; numberOfPassengers?: string; busCount?: string; transferType?: string; price?: string }>({});
  const [selectedDefultCompanyId, setSelectedDefultCompanyId] =
    useState<string>("");
      const [taxAmount, setTaxAmount] = useState<number>(0);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024; // 2 MB

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        return;
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        return;
      } else {
        try {
          setIsImageUploading(true);
          const uploadedImageUrl = await uploadImage(file);
          const updatedRows = [...productRows];
          updatedRows[index].productImg = uploadedImageUrl;
          setProductRows(updatedRows);
          setIsImageUploading(false);
        } catch (error) {
          showErrorMessage("Failed to upload the image.");
          setIsImageUploading(false);
        }
      }
    }
  };

  const { data: taxData, isLoading: taxLoading } = useGetTaxQuery({
    searchText: "",
  });

  const taxList = useMemo(() => {
    return taxData?.data as ITaxes[];
  }, [taxData?.data]);

   const companyList: ICompanyDetails[] = Array.isArray(companyData?.data)
      ? (companyData?.data as ICompanyDetails[])
      : [];

  // ── Location history (localStorage) ────────────────────────────────────────
  const [savedLocations, setSavedLocations] = useState<ISavedLocation[]>(() => getSavedLocations());
  const refreshSavedLocations = () => setSavedLocations(getSavedLocations());
  const savedDescriptions = useMemo(() => {
    const seen = new Set<string>();
    return savedLocations
      .filter(l => l.description && !seen.has(l.description) && seen.add(l.description))
      .map(l => l.description)
      .sort((a, b) => a.localeCompare(b));
  }, [savedLocations]);

  // ── Previous trips ───────────────────────────────────────────────────────────
  const [prevTripDialogOpen, setPrevTripDialogOpen] = useState(false);

  const [productRows, setProductRows] = useState<IProduct[]>([
    {
      productName: "",
      productDescription: "",
      productImg: "",
      numberOfPassengers: null,
      busCount: null,
      transferType: TransferType.OneWayTransfer,
      transferTypeFrom: "",
      transferTypeTo: "",
      transferTypeDescription: "",
      departureDateTime: null,
      returnDateTime: null,
      departureAddress: "",
      destinationAddress: "",
      multiDestination: "",
      quantity: 1,
      price: null,
      tax: 0,
      taxAmount: 0,
      total: 0,
      taxId: null,
      unitPriceWithDiscount: null,
      unitTotalWithDiscount: 0,
      productType: ProductType.Bus,
      productLongDescription: "",
      productShortDescription: "",
    },
  ]);
  const [discounts, setDiscounts] = useState<IDiscount[]>([
    { value: null, description: "" },
  ]);
  // ── Driver-aware time-change confirmation state ────────────────────────
  // Snapshot of the pickup/drop-off times we loaded from the server. When
  // the user submits, we compare the in-memory rows against this baseline
  // to know whether trip times changed. If a driver is already assigned and
  // any time changed, we ask the user to confirm before saving.
  type TimeBaseline = Record<string, string | null>;
  const initialTripTimesRef = React.useRef<TimeBaseline>({});
  const [driverConfirmOpen, setDriverConfirmOpen] = React.useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = React.useState<any>(null);
  const [pendingSubmitHelpers, setPendingSubmitHelpers] = React.useState<any>(null);

  const buildTripTimeBaseline = React.useCallback((rows: any[]): TimeBaseline => {
    const map: TimeBaseline = {};
    if (!Array.isArray(rows)) return map;
    rows.forEach((row, i) => {
      (["pickups", "dropoffs", "returnPickups", "returnDropoffs"] as const).forEach((g) => {
        const arr = Array.isArray(row?.[g]) ? row[g] : [];
        arr.forEach((s: any, j: number) => {
          const v = s?.dateTime;
          map[`${i}.${g}.${j}`] = v ? String(v) : null;
        });
      });
    });
    return map;
  }, []);

  const tripTimeKeysEqual = (a: any, b: any) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const ta = dayjs(a);
    const tb = dayjs(b);
    if (!ta.isValid() || !tb.isValid()) return String(a) === String(b);
    return ta.valueOf() === tb.valueOf();
  };

  const tripTimesChanged = React.useCallback((rows: any[]): boolean => {
    const next = buildTripTimeBaseline(rows);
    const prev = initialTripTimesRef.current || {};
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    for (const k of Array.from(keys)) {
      if (!tripTimeKeysEqual(prev[k], next[k])) return true;
    }
    return false;
  }, [buildTripTimeBaseline]);

  // Capture the baseline once the work order's sale data has been loaded and
  // mapped into productRows.
  React.useEffect(() => {
    if (productRows.length > 0 && Object.keys(initialTripTimesRef.current).length === 0) {
      initialTripTimesRef.current = buildTripTimeBaseline(productRows as any[]);
    }
  }, [productRows, buildTripTimeBaseline]);

  // True iff a driver is already assigned to this work order.
  const hasAssignedDriver = React.useMemo(() => {
    const wo: any = WorkOrderDataList;
    const a = wo?.assignDriver;
    if (!a) return false;
    let entries: any = a.assign_to;
    if (typeof entries === "string") {
      try { entries = JSON.parse(entries); } catch { entries = []; }
    }
    if (!Array.isArray(entries)) return false;
    return entries.some((e: any) => e && e.driverId);
  }, [WorkOrderDataList]);

  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountType, setDiscountType] = useState<"fixed" | "percent">("fixed");
  const [discountDescription, setDiscountDescription] = useState<string>("");
  const [subTotal, setSubTotal] = useState<number>(0);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [grandTotalEithDiscount, setGrandTotalWithDiscount] =
    useState<number>(0);
  const [selectedCompanyLogo, setSelectedCompanyLogo] = useState<string | null>(
    null
  );
  const [openCompany, setOpenCompany] = useState(false);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
    const [busCount, setBusCount] = useState<number | null>(null);

  const { data: workOrderLastData, isLoading: workOrderIsLoading } =
    useGetLastWorkOrderIdQuery();

  const {
    data: productData,
    isLoading: productIsLoading,
    refetch: productRefetch,
  } = useGetProductQuery({
    productBrandId: null,
    productCategoryId: null,
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    searchText: currentSearchQueryProduct,
    createdDate: null,
    id: null,
  });

  useEffect(() => {
    if (
      debouncedSearchQueryProduct.length >= 2 ||
      debouncedSearchQueryProduct === ""
    ) {
      setCurrentSearchQueryProduct(debouncedSearchQueryProduct);
      productRefetch();
    }
  }, [debouncedSearchQueryProduct, productRefetch]);

  const initialParams: IPriceHistoryPayload = {
    productName: "",
    transferTypeFrom: "",
    transferTypeTo: "",
  };

  const customerId = WorkOrderDataList?.customer?.id;
  const { data: prevTripsData } = useGetTripsByCustomerQuery(
    String(customerId || ""),
    { skip: !customerId }
  );
  const prevTrips: IPreviousTripItem[] = Array.isArray(prevTripsData?.data)
    ? (prevTripsData!.data as IPreviousTripItem[])
    : [];

  const {
    data: priceHistoryData,
    isLoading: priceHistoryIsLoading,
    isError: priceHistoryError,
    refetch: priceHistoryRefetch,
  } = useGetPriceHistoryQuery(
    currentPriceQueryParams ? currentPriceQueryParams : initialParams,
    {
      skip: false,
    }
  );

  useEffect(() => {
    if (
      priceHistoryData &&
      Array.isArray(priceHistoryData.data) &&
      priceHistoryData.data.length > 0
    ) {
      setAveragePrice(priceHistoryData.data[0]?.averagePrice || null);
      setNextPrice(priceHistoryData.data[0]?.price || null);
      setQuoteWorkOrderNoForPrice(
        priceHistoryData.data[0]?.quoteWorkOrderNo || null
      );
      setUpdated_at(priceHistoryData.data[0]?.updated_at || null);
      setPriceId(priceHistoryData.data[0]?.workOrderId || null);
      setBusCount(priceHistoryData.data[0]?.busCount || null); 
    } else {
      setAveragePrice(null);
      setNextPrice(null);
      setQuoteWorkOrderNoForPrice(null);
      setUpdated_at(null);
      setPriceId(null);
      setBusCount(null); 
    }
  }, [priceHistoryData]);

  useEffect(() => {
    if (WorkOrderDataList?.company?.companyName) {
      setSearchQuery(WorkOrderDataList.company.companyName);
    }
    if (WorkOrderDataList?.customer) {
     
      setSearchQueryCustomer(WorkOrderDataList?.customer?.customerCompanyName?.trim()
    ? WorkOrderDataList?.customer?.customerCompanyName
    : `${WorkOrderDataList?.customer?.customerFirstName || ""} ${WorkOrderDataList?.customer?.customerLastName || ""}`
      );
    }
    if (WorkOrderDataList?.company?.CompanyLogo) {
      setSelectedCompanyLogo(WorkOrderDataList.company.CompanyLogo);
    }
  }, [WorkOrderDataList]);

  const rawData = workOrderLastData?.data;
  const workOrderId =
    typeof rawData === "number"
      ? rawData
      : parseInt(rawData as string, 10) || 0;

  const generatedWorkOrderNo = generateWorkOrderNumber(workOrderId);

  useEffect(() => {
    const totalSubTotal = productRows.reduce((acc, row) => acc + row.total, 0);
    setSubTotal(totalSubTotal);

    const calculatedTotalDiscount = discounts.reduce(
      (acc, discount) => acc + (discount.value ?? 0),
      0
    );
    setTotalDiscount(calculatedTotalDiscount);

    const totalGrandTotal = totalSubTotal - calculatedTotalDiscount;
    setGrandTotal(totalGrandTotal);

    const unitPrice = calculatedTotalDiscount / totalSubTotal;
    setUnitPrice(unitPrice);

  



    const totalSubTotalWithDiscount = productRows.reduce(
      (acc, row) => acc + row.unitTotalWithDiscount,
      0
    );

  
   const calculatedHst = productRows.reduce((acc, row) => {
   return acc + (row.taxAmount ?? 0);
}, 0);
    setTaxAmount(calculatedHst);


    setGrandTotalWithDiscount(totalSubTotalWithDiscount);
  }, [productRows, discounts]);

  const productList: IProductPopUP[] = Array.isArray(productData?.data)
    ? (productData?.data as IProductPopUP[])
    : [];

  const today = dayjs();

  const handleProductSelect = (product: IProductPopUP, index: number) => {
    const updatedRows = [...productRows];
    updatedRows[index].productName = product.productName;
    updatedRows[index].productImg = product.productImg || "";

    setProductRows(updatedRows);
    setShowDropdownProductIndex(null);
    updatePriceQueryParams(updatedRows[index]);
  };

const handleAddRow = () => {
     setEditIndex(null);
     setPopupOpen(true);
     
     setProductRows((prev) => [
       ...prev,
       {
         productName: "",
         productDescription: "",
         productImg: "",
         numberOfPassengers: null,
         busCount: null,
         transferType: TransferType.OneWayTransfer,
         transferTypeFrom: "",
         transferTypeTo: "",
         transferTypeDescription: "",
         departureDateTime: null,
         returnDateTime: null,
         departureAddress: "",
         destinationAddress: "",
         multiDestination: "",
         quantity: 1,
         price: null,
         tax: 0,
         taxAmount: 0,
         total: 0,
         taxId: 1,
         unitPriceWithDiscount: null,
         unitTotalWithDiscount: 0,
         productType: ProductType.Bus,
         productLongDescription: "",
         productShortDescription: "",
         pickups: [{ address: "", city: "", dateTime: null }],
         dropoffs: [{ address: "", city: "", dateTime: null }],
         returnPickups: [{ address: "", city: "", dateTime: null }],
         returnDropoffs: [{ address: "", city: "", dateTime: null }],
       },
     ]);
     setSearchQueryProduct("");
   };
 
   const handlePriceChange = (index: number, newPrice: number) => {
     const updatedRows = [...productRows];
     const product = updatedRows[index];
     product.price = newPrice;

     // Bus rows multiply price × busCount (the user-entered quantity in the
     // form), matching the Quote/Create calculator. Falls back to `quantity`
     // for legacy non-bus rows.
     const buses = (product.busCount && product.busCount > 0)
       ? product.busCount
       : (product.quantity || 1);

     const newTaxAmount = newPrice * buses * (product.tax / 100);
     product.taxAmount = newTaxAmount;

     const newTotal = newPrice * buses + newTaxAmount;
     product.total = newPrice * buses;
     product.unitTotalWithDiscount = newTotal;

     setProductRows(updatedRows);
   };

 
   const updateTransferTypes = (updated: typeof productRows, productIndex: number) => {
   const product = updated[productIndex];
 
   if (!product.transferTypeFrom) {
     product.transferTypeFrom = product.pickups?.[0]?.city || "";
   }
 
   if (!product.transferTypeTo) {
     product.transferTypeTo = product.dropoffs?.[0]?.city || "";
   }
 };
 
 // Handle changes to items in pickups/dropoffs arrays
 const handleFieldChangeArray = (
   productIndex: number,
   field: "pickups" | "dropoffs" | "returnPickups" | "returnDropoffs",
   itemIndex: number,
   key: "address" | "city" | "dateTime" | "description",
   value: any
 ) => {
   const updated = [...productRows];
   if (!updated[productIndex][field]) updated[productIndex][field] = [];
 
   updated[productIndex][field]![itemIndex][key] = value;
    const product = updated[productIndex];
 
   // Update transferTypeFrom/To only if field is pickups or dropoffs
   if (field === "pickups" || field === "dropoffs") {
     updateTransferTypes(updated, productIndex);
   }
 
  if (product.transferType !== TransferType.OneWayTransfer) {
     if (field === "pickups" && (!product.returnPickups || product.returnPickups.length === 0)) {
       product.returnPickups = (product.pickups || []).map(p => ({ ...p }));
     }
     if (field === "dropoffs" && (!product.returnDropoffs || product.returnDropoffs.length === 0)) {
       product.returnDropoffs = (product.dropoffs || []).map(d => ({ ...d }));
     }
   }
   
 
   setProductRows(updated);
 };
 
 // Add a new item to pickups/dropoffs arrays
 const addArrayItem = (
   productIndex: number,
   field: "pickups" | "dropoffs" | "returnPickups" | "returnDropoffs"
 ) => {
   const updated = [...productRows];
   if (!updated[productIndex][field]) updated[productIndex][field] = [];
 
   updated[productIndex][field]!.push({ address: "", city: "", dateTime: null });
 
   if (field === "pickups" || field === "dropoffs") {
     updateTransferTypes(updated, productIndex);
   }
 
   setProductRows(updated);
 };
 
 // Remove an item from pickups/dropoffs arrays
 const removeArrayItem = (
   productIndex: number,
   field: "pickups" | "dropoffs" | "returnPickups" | "returnDropoffs",
   itemIndex: number
 ) => {
   const updated = [...productRows];
   updated[productIndex][field]?.splice(itemIndex, 1);
 
   if (field === "pickups" || field === "dropoffs") {
     updateTransferTypes(updated, productIndex);
   }
 
   setProductRows(updated);
 };
 

   const handleSaveProduct = () => {
       const currentRow = productRows[editIndex !== null ? editIndex : productRows.length - 1];
       let newErrors: { departureDateTime?: string; returnDateTime?: string; productName?: string; numberOfPassengers?: string; busCount?: string; transferType?: string; price?: string } = {};

       // Required fields
       if (!currentRow?.productName || String(currentRow.productName).trim() === "") {
         newErrors.productName = "Bus / Service is required";
       }
       if (!currentRow?.numberOfPassengers || Number(currentRow.numberOfPassengers) <= 0) {
         newErrors.numberOfPassengers = "Passengers is required";
       }
       if (!currentRow?.busCount || Number(currentRow.busCount) <= 0) {
         newErrors.busCount = "Buses (Qty) is required";
       }
       if (!currentRow?.transferType || String(currentRow.transferType).trim() === "") {
         newErrors.transferType = "Transfer Type is required";
       }
       if (currentRow?.price === undefined || currentRow?.price === null || Number(currentRow.price) <= 0) {
         newErrors.price = "Trip Price is required";
       }

       // Validate Departure Date & Time — check new array format first, fallback to flat field
       const hasDepartureTime =
         currentRow?.departureDateTime ||
         (Array.isArray(currentRow?.pickups) && currentRow.pickups.some((p) => p.dateTime));
       if (!hasDepartureTime) {
         newErrors.departureDateTime = "Departure Date & Time is required";
       }

       // Validate Return Date & Time if Transfer Type is NOT One-Way
       const hasReturnTime =
         currentRow?.returnDateTime ||
         (Array.isArray(currentRow?.returnPickups) && currentRow.returnPickups.some((p) => p.dateTime));
       if (currentRow?.transferType !== TransferType.OneWayTransfer && !hasReturnTime) {
         newErrors.returnDateTime = "Return Date & Time is required";
       }

       // If there are errors, update the state and stop submission
       if (Object.keys(newErrors).length > 0) {
         setErrors(newErrors);
         return;
       }
     
       // Clear errors if all validations pass
       setErrors({});
     
       if (editIndex !== null) {
         // Edit existing row
         const updatedRows = [...productRows];
         updatedRows[editIndex] = currentRow;
         setProductRows(updatedRows);
       } else {
         // Add new product
         setProductRows([...productRows]);
       }
     
       setPopupOpen(false);
     };
 
  const handleApplyPreviousTrip = (trip: IPreviousTripItem) => {
    const toDayjs = (v: string | null | undefined) => (v ? dayjs(v) : null);
    const mapStops = (stops?: { address: string; city: string; dateTime: string | null; description?: string }[]) =>
      (stops || [{ address: "", city: "", dateTime: null }]).map((s) => ({
        address: s.address || "",
        city: s.city || "",
        dateTime: null,
        description: s.description || "",
      }));
    const updated = [...productRows];
    updated[rowIdx] = {
      ...updated[rowIdx],
      productName:             trip.productName || "",
      productDescription:      trip.productDescription || "",
      productShortDescription: trip.productShortDescription || "",
      productLongDescription:  trip.productLongDescription || "",
      numberOfPassengers:      trip.numberOfPassengers,
      busCount:                trip.busCount,
      transferType:            trip.transferType || "OneWayTransfer",
      transferTypeFrom:        trip.transferTypeFrom || "",
      transferTypeTo:          trip.transferTypeTo || "",
      transferTypeDescription: trip.transferTypeDescription || "",
      price:                   trip.price,
      tax:                     trip.tax ?? 0,
      taxId:                   trip.taxId,
      taxAmount:               trip.taxAmount ?? 0,
      total:                   trip.total ?? 0,
      productType:             trip.productType || "",
      useSameAsOutbound:       trip.useSameAsOutbound,
      pickups:                 mapStops(trip.pickups),
      dropoffs:                mapStops(trip.dropoffs),
      returnPickups:           mapStops(trip.returnPickups),
      returnDropoffs:          mapStops(trip.returnDropoffs),
      departureDateTime:       null,
      returnDateTime:          null,
    };
    setProductRows(updated);
    setPrevTripDialogOpen(false);
  };

   const handleCancel = () => {
     if (editIndex !== null && originalProduct) {
       // Log the original product row before canceling
       console.log("Reverting to the original product row:", originalProduct);
   
       // Revert to the original product data (before editing)
       setProductRows(prevRows => {
         const updatedRows = [...prevRows];
         updatedRows[editIndex] = originalProduct; // Restore the original row
         return updatedRows;
       });
   
     
       setEditIndex(null);    // Clear edit index
       setOriginalProduct(null);  // Clear original product data after canceling
     } else {
       // If no row is being edited, just close the popup and remove the last empty row if necessary
       const lastRow = productRows[productRows.length - 1];
       if (
         !lastRow?.productName ||
         !lastRow?.productDescription ||
         !lastRow?.price ||
         (lastRow?.tax && lastRow?.tax <= 0)
       ) {
         console.log("Removing last row because it's empty or invalid");
         setProductRows(productRows.slice(0, productRows.length - 1));
       }
     }
     setPopupOpen(false);  // Close the popup
   };

  const handleFieldChange = <K extends keyof IProduct>(
    index: number,
    field: K,
    value: IProduct[K]
  ) => {
    const updatedRows = [...productRows];
    updatedRows[index][field] = value;

    const price = updatedRows[index].price ?? 0;
    // Match Quote/Create: bus rows use busCount as the quantity multiplier.
    const busCount = (updatedRows[index].busCount && updatedRows[index].busCount! > 0)
      ? updatedRows[index].busCount!
      : (updatedRows[index].quantity ?? 1);
    const tax = updatedRows[index].tax ?? 0;

    updatedRows[index].total = price * busCount;

    const totalSubTotal = updatedRows.reduce((acc, row) => acc + row.total, 0);

    const totalDiscountValue = discounts.reduce(
      (acc, discount) => acc + (discount.value ?? 0),
      0
    );

    const unitPriceDiscount = totalSubTotal > 0 ? totalDiscountValue / totalSubTotal : 0;

    const priceWithDiscount = price * (1 - unitPriceDiscount);

    const unitProductPriceWithDiscountAndTax =
      priceWithDiscount * (1 + tax / 100);

    const totalWithDiscountAndTax =
      priceWithDiscount * busCount * (1 + tax / 100);

    updatedRows[index].unitPriceWithDiscount =
      unitProductPriceWithDiscountAndTax;
    updatedRows[index].unitTotalWithDiscount = totalWithDiscountAndTax;
    updatedRows[index].taxAmount = priceWithDiscount * busCount * (tax / 100);

    setProductRows(updatedRows);

    const priceAffectingFields = ["productName", "transferTypeFrom", "transferTypeTo"] as const;
    if (
      (priceAffectingFields as readonly string[]).includes(field as string) &&
      updatedRows[index].productName &&
      updatedRows[index].transferTypeFrom &&
      updatedRows[index].transferTypeTo
    ) {
      updatePriceQueryParams(updatedRows[index]);
      setCurrentIndex(index);
    }
  };

  const updatePriceQueryParams = (row: IProduct) => {
    const { productName, transferTypeFrom, transferTypeTo } = row;

    if (productName && transferTypeFrom && transferTypeTo) {
      const newParams: IPriceHistoryPayload = {
        productName,
        transferTypeFrom,
        transferTypeTo,
      };

      setCurrentPriceQueryParams(newParams);

      if (newParams) {
        priceHistoryRefetch();
      }
    } else {
      console.error("Incomplete parameters for price query.");
    }
  };

  useEffect(() => {
    if (productRows.length > 0) {
      const totalSubTotal = productRows.reduce(
        (acc, row) => acc + row.total,
        0
      );
      setSubTotal(totalSubTotal);

      const calculatedTotalDiscount = discounts.reduce(
        (acc, discount) => acc + (discount.value ?? 0),
        0
      );
      setTotalDiscount(calculatedTotalDiscount);

      if (totalSubTotal > 0) {
        const unitPriceDiscount = totalDiscount / totalSubTotal;
        setUnitPrice(unitPriceDiscount);

        const updatedRows = productRows.map((row) => {
          const price = row.price ?? 0;
          // Bus rows multiply by busCount (matches Quote/Create calculator).
          const busCount = (row.busCount && row.busCount > 0)
            ? row.busCount
            : (row.quantity ?? 1);
          const tax = row.tax ?? 0;

          const priceWithDiscount = price * (1 - unitPriceDiscount);
          const unitProductPriceWithDiscountAndTax =
            priceWithDiscount * (1 + tax / 100);
          const totalWithDiscountAndTax =
            priceWithDiscount * busCount * (1 + tax / 100);
          const taxAmountWithDiscount = priceWithDiscount * busCount * (tax / 100);

          return {
            ...row,
            unitPriceWithDiscount: unitProductPriceWithDiscountAndTax,
            unitTotalWithDiscount: totalWithDiscountAndTax,
            taxAmount: taxAmountWithDiscount, // Updating taxAmount after discount
          };
        });

        setProductRows((prevRows) => {
          // Only update the state if rows have actually changed
          if (JSON.stringify(prevRows) !== JSON.stringify(updatedRows)) {
            return updatedRows;
          }
          return prevRows;
        });

        const totalSubTotalWithDiscount = updatedRows.reduce(
          (acc, row) => acc + row.unitTotalWithDiscount,
          0
        );
        setGrandTotalWithDiscount(totalSubTotalWithDiscount);
      }
    }
  }, [productRows, discounts, totalDiscount]);

  const handleProductNameChange = (index: number, value: string) => {
    setSearchQueryProduct(value);
    handleFieldChange(index, "productName", value);
  };

  

  const handleDeleteRow = (index: number) => {
    // const updatedRows = productRows.filter((_, i) => i !== index);
     const updatedRows = [...productRows];
     updatedRows.splice(index, 1);
     setProductRows(updatedRows);
    // console.log(updatedRows)
   };

   const handleEditRow = (index: number) => {
    const productToEdit = productRows[index];
    setOriginalProduct({ ...productToEdit });
    setEditIndex(index);
    setPopupOpen(true);
    //console.log("Editing product: ", productToEdit);

  };

  const formatAddress = (
    streetNumber: string,
    streetName: string,
    postalCode: string,
    city: string,
    province: string
  ) => {

    if (!streetNumber && !streetName && !city && !province && !postalCode) {
      return ""; // Return an empty string if all fields are empty
    }
    return `${streetNumber} ${streetName},\n${city}, ${province}, ${postalCode}`;
  };


  useEffect(() => {
      if (companyList && companyList.length > 0) {
        const defaultCompany = companyList.find(
          (company) => company.isDefault === 1
        );
  
        const selectedCompany = defaultCompany || companyList[0];
  
        if (selectedCompany) {
          setSelectedDefultCompanyId(String(selectedCompany.id));
  
          const formattedAddress = formatAddress(
            selectedCompany.companyStreetNumber,
            selectedCompany.companyStreetName,
            selectedCompany.companyPostalCode,
            selectedCompany.companyCity,
            selectedCompany.companyProvince
          );
  
          formik.setFieldValue("companyName", selectedCompany.companyName);
          formik.setFieldValue(
            "companyProvince",
            selectedCompany.companyProvince
          );
          formik.setFieldValue("companyCity", selectedCompany.companyCity);
          formik.setFieldValue(
            "companyPostalCode",
            selectedCompany.companyPostalCode
          );
          formik.setFieldValue(
            "companyStreetNumber",
            selectedCompany.companyStreetNumber
          );
          formik.setFieldValue(
            "companyStreetName",
            selectedCompany.companyStreetName
          );
          formik.setFieldValue(
            "companyMobileNumber",
            selectedCompany.companyMobileNumber
          );
          formik.setFieldValue("companyEmail", selectedCompany.companyEmail);
          formik.setFieldValue("quoteWorkOrderCompanyId", selectedCompany.id);
          formik.setFieldValue("companyAddress", formattedAddress);
          setSelectedCompanyLogo(selectedCompany.CompanyLogo);
        }
      }
    }, [companyList]);

  const handleDiscountChange = (
    index: number,
    field: keyof IDiscount,
    value: any
  ) => {
    const updatedDiscounts = [...discounts];
    updatedDiscounts[index] = {
      ...updatedDiscounts[index],
      [field]: value,
    };
    setDiscounts(updatedDiscounts);
  };

  const handleOpenDialog = (index: number | null) => {
    if (index !== null) {
      const discountToEdit = discounts[index];
      setDiscountValue(discountToEdit.value ?? 0);
      setDiscountType(discountToEdit.type ?? "fixed");
      setDiscountDescription(discountToEdit.description);
      setEditingIndex(index);
    } else {
      setDiscountValue(0);
      setDiscountType("fixed");
      setDiscountDescription("");
      setEditingIndex(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDiscountValue(0);
    setDiscountType("fixed");
    setDiscountDescription("");
    setEditingIndex(null);
  };

  const handleSaveDiscount = () => {
    if (editingIndex !== null) {
      const updatedDiscounts = discounts.map((discount, index) =>
        index === editingIndex
          ? { value: discountValue, type: discountType, description: discountDescription }
          : discount
      );
      setDiscounts(updatedDiscounts);
    } else {
      setDiscounts((prev) => [
        ...prev,
        { value: discountValue, type: discountType, description: discountDescription },
      ]);
    }
    handleCloseDialog();
  };

  const handleDeleteDiscount = (index: number) => {
    const updatedDiscounts = discounts.filter((_, i) => i !== index);
    setDiscounts(updatedDiscounts);
  };

  const formatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName},\n${city}, ${province}, ${postalCode}`;
  };

  // const formatCustomerAddressReadOnly = (
  //   streetNumber: string,
  //   streetName: string,
  //   city: string,
  //   province: string,
  //   postalCode: string,
  // ) => {
  //   return `${streetNumber} ${streetName},\n${city}, ${province}, ${postalCode}`;
  // };

   const formatCustomerAddressReadOnly = (
  streetNumber: string,
  streetName: string,
  city: string,
  province: string,
  postalCode: string
) => {
  const line1 = [streetNumber, streetName]
    .filter(Boolean)
    .join(" ");

  const line2 = [city, province, postalCode]
    .filter(Boolean)
    .join(", ");

  return [line1, line2]
    .filter(Boolean)
    .join("\n");
};


  const initialCompanyAddress = formatAddressReadOnly(
    WorkOrderDataList?.company.companyStreetNumber || "",
    WorkOrderDataList?.company.companyStreetName || "",
    WorkOrderDataList?.company.companyCity || "",
    WorkOrderDataList?.company.companyProvince || "",
    WorkOrderDataList?.company.companyPostalCode || "",
  );

  const initialCustomerAddress = formatCustomerAddressReadOnly(
    WorkOrderDataList?.customer.customerStreetNumber || "",
    WorkOrderDataList?.customer.customerStreetName || "",
    WorkOrderDataList?.customer.customerCity || "",
    WorkOrderDataList?.customer.customerProvince || "",
    WorkOrderDataList?.customer.customerPostalCode || "",
  );

  const formik = useFormik({
    initialValues: {
      id: WorkOrderDataList?.id,
      companyName: WorkOrderDataList?.company.companyName || "",
      companyLogo: WorkOrderDataList?.company.CompanyLogo || "",
      companyProvince: WorkOrderDataList?.company.companyProvince || "",
      companyCity: WorkOrderDataList?.company.companyCity || "",
      companyPostalCode: WorkOrderDataList?.company.companyPostalCode || "",
      companyStreetNumber: WorkOrderDataList?.company.companyStreetNumber || "",
      companyStreetName: WorkOrderDataList?.company.companyStreetName || "",
      companyMobileNumber: WorkOrderDataList?.company.companyMobileNumber || "",
      companyEmail: WorkOrderDataList?.company.companyEmail || "",
      companyAddress: initialCompanyAddress,
      customerCompanyName:
        WorkOrderDataList?.customer.customerCompanyName || "",
         customerFirstName: WorkOrderDataList?.customer.customerFirstName || "",
      customerLastName: WorkOrderDataList?.customer.customerLastName || "",
      customerMobileNum: WorkOrderDataList?.customer.customerMobileNum || "",
      customerEmail: WorkOrderDataList?.customer.customerEmail || "",
     

      customerProvince: WorkOrderDataList?. customer. customerProvince || "",
      customerCity: WorkOrderDataList?. customer. customerCity || "",
      customerPostalCode: WorkOrderDataList?. customer. customerPostalCode || "",
      customerStreetNumber: WorkOrderDataList?. customer. customerStreetNumber || "",
      customerStreetName: WorkOrderDataList?. customer. customerStreetName || "",

      customerAddress: initialCustomerAddress,
      
      quoteWorkOrderNo: WorkOrderDataList?.quoteWorkOrderNo || "",
      quoteWorkOrderIssueDate: dayjs(
        WorkOrderDataList?.quoteWorkOrderIssueDate
      ),
      quoteWorkOrderDueDate: dayjs(WorkOrderDataList?.quoteWorkOrderDueDate),
      quoteTripId: WorkOrderDataList?.quoteTripId,
      quoteTripName: WorkOrderDataList?.quoteTripName,
      //quoteWorkOrderStatus: WorkOrderDataList?.quoteWorkOrderStatus || "",
      quoteWorkOrderStatus: WorkOrderDataList?.quoteWorkOrderStatus ?? "",
      quoteWorkOrderSale: [],
      quoteWorkOrderSaleDiscount:
        WorkOrderDataList?.quoteWorkOrderSaleDiscount || "",
      quoteWorkOrderSaleSubTotal:
        WorkOrderDataList?.quoteWorkOrderSaleSubTotal || "",
      quoteWorkOrderSaleGrandTotal:
        WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "",
      quoteWorkOrderSaleDiscountDescription: [],
      quoteWorkOrderCompanyId: WorkOrderDataList?.quoteWorkOrderCompanyId || "",
      quoteWorkOrderCustomerId:
        WorkOrderDataList?.quoteWorkOrderCustomerId || "",
      quoteWorkOrderCustomerRemark:
        WorkOrderDataList?.quoteWorkOrderCustomerRemark || "",
      quoteWorkOrderCustomerMemo:
        WorkOrderDataList?.quoteWorkOrderCustomerMemo || "",
      po_number: displayPoNumber,
      InvoiceNo: displayInvoiceNo,
    },

    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      return errors;
    },

    onSubmit: async (values, helpers) => {
      // Intercept the save when a driver is already assigned and the user
      // has changed any pickup/drop-off time. Surfacing this confirmation
      // here keeps the rest of the save pipeline unchanged — Continue just
      // resumes onSubmit via runSubmit().
      if (hasAssignedDriver && tripTimesChanged(productRows as any[])) {
        setPendingSubmitValues(values);
        setPendingSubmitHelpers(helpers);
        setDriverConfirmOpen(true);
        return;
      }
      await runSubmit(values, helpers);
    },
  });

  const runSubmit = async (values: any, helpers: any) => {
    const { resetForm } = helpers;
    try {
        // Backfill backend-required row fields so editing pickup time
        // doesn't strip productName / transferTypeFrom / transferTypeTo.
        const normalizedRows = productRows.map((row: any) => {
          const firstPickup = row?.pickups?.[0];
          const firstDropoff = row?.dropoffs?.[0];
          return {
            ...row,
            productName:
              (row.productName && String(row.productName).trim()) ||
              row.productType ||
              "Service",
            transferTypeFrom:
              (row.transferTypeFrom && String(row.transferTypeFrom).trim()) ||
              firstPickup?.city ||
              firstPickup?.address ||
              "—",
            transferTypeTo:
              (row.transferTypeTo && String(row.transferTypeTo).trim()) ||
              firstDropoff?.city ||
              firstDropoff?.address ||
              "—",
          };
        });
        const temData = {
          id: values.id,
          companyName: values.companyName,
          companyLogo: values.companyLogo,
          companyProvince: values.companyProvince,
          companyCity: values.companyCity,
          companyPostalCode: values.companyPostalCode,
          companyStreetNumber: values.companyStreetNumber,
          companyStreetName: values.companyStreetName,
          companyMobileNumber: values.companyMobileNumber,
          customerMobileNum: values.customerMobileNum,
          companyEmail: values.companyEmail,
          companyAddress: values.companyAddress,
          quoteWorkOrderNo: values.quoteWorkOrderNo,
          quoteTripId: values.quoteTripId,
          quoteTripName: values.quoteTripName,
          quoteWorkOrderIssueDate: values.quoteWorkOrderIssueDate,
          quoteWorkOrderDueDate: values.quoteWorkOrderDueDate,
          quoteWorkOrderStatus: String(values.quoteWorkOrderStatus),
          customerCompanyName: values.customerCompanyName,
             customerFirstName: values.customerFirstName,
          customerLastName: values.customerLastName,
          customerEmail: values.customerEmail,

         customerProvince: values.customerProvince,
          customerCity: values.customerCity,
          customerPostalCode: values.customerPostalCode,
          customerStreetNumber: values.customerStreetNumber,
          customerStreetName: values.customerStreetName,

          // Persist datetimes as Toronto wall-clock time (no UTC Z suffix)
          quoteWorkOrderSale: normalizeDateTimeFields(normalizedRows),
          quoteWorkOrderDiscount: String(totalDiscount),
          quoteWorkOrderSubTotal: String(subTotal),
          quoteWorkOrderSaleGrandTotal: String(grandTotalEithDiscount),
          quoteWorkOrderSaleDiscountDescription: discounts,
          quoteWorkOrderCompanyId: String(values.quoteWorkOrderCompanyId),
          quoteWorkOrderCustomerId: String(values.quoteWorkOrderCustomerId),
          quoteWorkOrderCustomerRemark: values.quoteWorkOrderCustomerRemark,
          quoteWorkOrderCustomerMemo: values.quoteWorkOrderCustomerMemo,
        };

        // If this quote has already been converted to an invoice, the backend
        // rejects work-order edits ("covered by an invoice"). Update only the
        // invoice in that case; otherwise update the work order as usual.
        if (hasInvoice && invoiceRecordId) {
          const invoicePayload = {
            ...temData,
            id: invoiceRecordId,
            po_number: values.po_number || "",
          };
          const invoiceResponse = await editInvoiceMutation(invoicePayload).unwrap();
          if (!invoiceResponse.status) {
            showErrorMessage(invoiceResponse.message);
            return;
          }
          showMessage(invoiceResponse.message || "Invoice updated successfully.");
        } else {
          const workOrderResponse = await newTax(temData).unwrap();
          if (!workOrderResponse.status) {
            showErrorMessage(workOrderResponse.message);
            return;
          }
          showMessage(workOrderResponse.message);
        }
        navigate(-1);
    } catch (error: any) {
      const data = error?.data || error;
      const msg = data?.message || error?.error || "Something went wrong";
      // Backend conflict: another driver trip overlaps the new times.
      // Surface the message and keep the page open so the user can adjust.
      if (data?.type === "driver_time_conflict") {
        showErrorMessage(msg);
        return;
      }
      showErrorMessage(msg);
    }
  };

  const formValid = useMemo(() => {
    return formik.values.companyName === "" ||
      formik.values.companyName === undefined ||
      formik.values.customerMobileNum === "" ||
      formik.values.customerMobileNum === undefined ||
      formik.values.customerCompanyName === "" ||
      formik.values.customerCompanyName === undefined ||
      formik.values.quoteWorkOrderStatus === "" ||
      formik.values.quoteWorkOrderStatus === undefined
      ? false
      : true;
  }, [formik]);

  if (
    isLoading ||
    isInvoiceSaving ||
    taxLoading ||
    workOrderIsLoading ||
    productIsLoading ||
    workIsLoading
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }




  const handleSelect = (companyId: string) => {
    const selectedCompany = companyList.find(
      (company) => String(company.id) === companyId
    );
    if (selectedCompany) {
      // setSearchQuery(selectedCompany.companyName);

      const formattedAddress = formatAddress(
        selectedCompany.companyStreetNumber,
        selectedCompany.companyStreetName,
        selectedCompany.companyCity,
        selectedCompany.companyProvince,
        selectedCompany.companyPostalCode,
      );

      formik.setFieldValue("companyName", selectedCompany.companyName);
      formik.setFieldValue("companyProvince", selectedCompany.companyProvince);
      formik.setFieldValue("companyCity", selectedCompany.companyCity);
      formik.setFieldValue(
        "companyPostalCode",
        selectedCompany.companyPostalCode
      );
      formik.setFieldValue(
        "companyStreetNumber",
        selectedCompany.companyStreetNumber
      );
      formik.setFieldValue(
        "companyStreetName",
        selectedCompany.companyStreetName
      );

      formik.setFieldValue(
        "companyMobileNumber",
        selectedCompany.companyMobileNumber
      );

      formik.setFieldValue("companyEmail", selectedCompany.companyEmail);
      formik.setFieldValue("quoteWorkOrderCompanyId", selectedCompany.id);

      formik.setFieldValue("companyAddress", formattedAddress);
      setSelectedCompanyLogo(selectedCompany.CompanyLogo);
    }
  };

  const rowIdx = editIndex !== null ? editIndex : productRows.length - 1;
  const currentRow = productRows[rowIdx];

  const sectionCard = { bgcolor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 2, mb: 3, overflow: "hidden" } as const;
  const sectionHeader = { px: 3, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" } as const;
  const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase" as const, letterSpacing: 0.8 };
  const infoLabel = quoteInfoLabel;
  const InfoBlock = QuoteInfoBlock;

  // Date validation helpers for the trip dialog.
  // Returns undefined when no valid dates exist → no minimum constraint on the picker.
  const getLatestValidDate = (dates: (any | null | undefined)[]): dayjs.Dayjs | undefined => {
    const valid = dates.filter(d => d && dayjs(d).isValid());
    if (valid.length === 0) return undefined;
    return valid.reduce<dayjs.Dayjs>((latest, d) => dayjs(d).isAfter(latest) ? dayjs(d) : latest, dayjs(valid[0]));
  };
  const dropoffMinDate      = getLatestValidDate((currentRow?.pickups       || []).map(p => p.dateTime));
  const returnPickupMinDate = getLatestValidDate((currentRow?.dropoffs      || []).map(d => d.dateTime));
  const returnDropoffMinDate= getLatestValidDate((currentRow?.returnPickups || []).map(p => p.dateTime));

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh", pb: 4 }}>
      <form onSubmit={formik.handleSubmit}>
          {/* ── Page Header ── */}
          <Box sx={{ px: 3, pt: 3, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>{hasInvoice ? "Edit Invoice" : "Edit Quote"}</Typography>
              {formik.values.quoteWorkOrderNo && (
                <Box sx={{ bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2563eb" }}>#{formik.values.quoteWorkOrderNo}</Typography>
                </Box>
              )}
              {hasInvoice && formik.values.InvoiceNo && (
                <Box sx={{ bgcolor: "#ecfdf5", border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>Invoice {formik.values.InvoiceNo}</Typography>
                </Box>
              )}
              {!!WorkOrderDataList?.isInvoice && (
                <Alert severity="success" sx={{ py: 0, px: 1.5 }}>Converted to invoice</Alert>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<ArrowBackIosIcon />} onClick={() => navigate(-1)} sx={{ borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}>Back</Button>
              <Button variant="contained" startIcon={<SaveAltIcon />} onClick={() => formik.handleSubmit()} disabled={isLoading} sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}>Update</Button>
            </Box>
          </Box>

          <Box sx={{ px: 3 }}>

            {/* ── Section 1: Quote Details ── */}
            <Box sx={sectionCard}>
              <Box sx={sectionHeader}>
                <Typography sx={sectionTitle}>{hasInvoice ? "Invoice Details" : "Quote Details"}</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item lg={2} md={3} sm={4} xs={12}>
                    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80, bgcolor: "#f8fafc" }}>
                      {selectedCompanyLogo ? (
                        <img src={`${LARAVEL_API_URL}/${selectedCompanyLogo}`} alt="Company Logo" style={{ maxWidth: "100%", maxHeight: 70, objectFit: "contain" }} />
                      ) : (
                        <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 28 }}>
                          {(formik.values.companyName || "C").charAt(0).toUpperCase()}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item lg={5} md={4} sm={8} xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <InfoBlock label="Quote Number" value={formik.values.quoteWorkOrderNo} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InfoBlock label="Trip ID" value={(() => {
                          const co = companyList.find((c) => String(c.id) === String(formik.values.quoteWorkOrderCompanyId));
                          const prefix = (co as any)?.companyTripPrefix || "";
                          const raw = formik.values.quoteTripId;
                          if (!raw) return "";
                          return prefix ? `${prefix}${String(raw).padStart(5, "0")}` : String(raw);
                        })()} />
                      </Grid>
                      {hasInvoice && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <InfoBlock label="Invoice Number" value={formik.values.InvoiceNo} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ ...infoLabel, mb: 0.5 }}>PO Number</Typography>
                            <TextField
                              placeholder="Enter PO Number"
                              size="small"
                              fullWidth
                              {...formik.getFieldProps("po_number")}
                              InputProps={{ sx: { fontSize: 14 } }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item lg={5} md={5} sm={12} xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ ...infoLabel, mb: 0.5 }}>Issue Date</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={formik.values.quoteWorkOrderIssueDate}
                            onChange={(date) => formik.setFieldValue("quoteWorkOrderIssueDate", date)}
                            slotProps={{ textField: { size: "small", sx: { width: "100%" } } }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ ...infoLabel, mb: 0.5 }}>Trip Name</Typography>
                        <TextField
                          placeholder="Enter trip name"
                          size="small"
                          fullWidth
                          {...formik.getFieldProps("quoteTripName")}
                          InputProps={{ sx: { fontSize: 14 } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* ── Section 2: Parties ── */}
            <Box sx={sectionCard}>
              <Box sx={sectionHeader}>
                <Typography sx={sectionTitle}>Parties</Typography>
              </Box>
              <Box sx={{ p: 0 }}>
                <Grid container>
                  {/* Issue From */}
                  <Grid item lg={6} md={6} sm={12} xs={12} sx={{ borderRight: { md: "1px solid #e2e8f0" } }}>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>Issue From</Typography>
                        <TextField
                          select
                          size="small"
                          value={selectedDefultCompanyId}
                          onChange={(event) => {
                            const selectedId = event.target.value;
                            setSelectedDefultCompanyId(selectedId);
                            handleSelect(selectedId);
                          }}
                          sx={{ minWidth: 160 }}
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="" disabled style={{ color: "gray" }}>Select company</MenuItem>
                          {companyList && companyList.map((company) => (
                            <MenuItem key={company.id} value={String(company.id)}>{company.companyName}</MenuItem>
                          ))}
                        </TextField>
                      </Box>

              {/* REST OF ISSUE FROM - INFOBLOCKS */}
              <Grid container spacing={2}>
                        <Grid item xs={12}><InfoBlock label="Company Name" value={formik.values.companyName} /></Grid>
                        <Grid item xs={12}><InfoBlock label="Address" value={formik.values.companyAddress} /></Grid>
                        <Grid item xs={6}><InfoBlock label="Phone" value={formik.values.companyMobileNumber} /></Grid>
                        <Grid item xs={6}><InfoBlock label="Email" value={formik.values.companyEmail} /></Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  {/* Issue For */}
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>Issue For</Typography>
                        <TextField
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CiSearch style={{ color: "#94a3b8", fontSize: 18 }} />
                              </InputAdornment>
                            ),
                            readOnly: true,
                          }}
                          value={searchQueryCustomer}
                          onChange={(e) => {
                            setSearchQueryCustomer(e.target.value);
                            setShowDropdownCustomer(true);
                          }}
                          placeholder="Search customer..."
                          size="small"
                          sx={{ minWidth: 200 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <InfoBlock
                            label="Name"
                            value={
                              formik.values.customerCompanyName?.trim()
                                ? formik.values.customerCompanyName
                                : `${formik.values.customerFirstName || ""} ${formik.values.customerLastName || ""}`.trim()
                            }
                          />
                        </Grid>
                        <Grid item xs={6}><InfoBlock label="Phone" value={formik.values.customerMobileNum} /></Grid>
                        <Grid item xs={6}><InfoBlock label="Email" value={formik.values.customerEmail} /></Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* ── Section 3: Services ── */}
            <Box sx={sectionCard}>
              <Box sx={sectionHeader}>
                <Typography sx={sectionTitle}>Services</Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddRow} sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" }, fontSize: 13 }}>Add Trip</Button>
              </Box>
              <Box sx={{ p: 0 }}>
                {productRows.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No trips added yet. Click "Add Trip" to start.</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>Bus & Services</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>Trip Information</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productRows.map((product, index) => (
                          <TableRow key={index} sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "#f8fafc" } }}>
                            <TableCell sx={{ verticalAlign: "top", borderBottom: "1px solid #f1f5f9", py: 2 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{product.productName || ""}</Typography>
                              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>{product.productDescription || ""}</Typography>
                              {(product.numberOfPassengers || product.busCount) && (
                                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                                  {product.numberOfPassengers ? `${product.numberOfPassengers} pax` : ""}
                                  {product.numberOfPassengers && product.busCount ? " · " : ""}
                                  {product.busCount ? `${product.busCount} bus` : ""}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top", borderBottom: "1px solid #f1f5f9", py: 2 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
                                {product.transferType ? (Object.keys(TransferType) as (keyof typeof TransferType)[]).find((key) => TransferType[key] === String(product.transferType)) : ""}
                                {product.transferTypeFrom && product.transferTypeTo ? ` (${product.transferTypeFrom} → ${product.transferTypeTo})` : ""}
                              </Typography>
                              {/* New array-based pickup/dropoff display */}
                              {(product.pickups || []).map((pickup, pi) => (
                                (pickup.address || (pickup.dateTime && dayjs(pickup.dateTime).isValid())) ? (
                                  <Typography key={pi} sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                    <strong>Pickup{(product.pickups?.length || 0) > 1 ? ` ${pi + 1}` : ""}:</strong>{" "}
                                    {pickup.address}{pickup.city ? `, ${pickup.city}` : ""}
                                    {pickup.dateTime && dayjs(pickup.dateTime).isValid() && <> · {dayjs(pickup.dateTime).format("DD-MMM-YY hh:mm A")}</>}
                                  </Typography>
                                ) : null
                              ))}
                              {(product.dropoffs || []).map((drop, di) => (
                                (drop.address || (drop.dateTime && dayjs(drop.dateTime).isValid())) ? (
                                  <Typography key={di} sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                    <strong>Dropoff{(product.dropoffs?.length || 0) > 1 ? ` ${di + 1}` : ""}:</strong>{" "}
                                    {drop.address}{drop.city ? `, ${drop.city}` : ""}
                                    {drop.dateTime && dayjs(drop.dateTime).isValid() && <> · {dayjs(drop.dateTime).format("DD-MMM-YY hh:mm A")}</>}
                                  </Typography>
                                ) : null
                              ))}
                              {(product.returnPickups || []).map((pickup, pi) => (
                                (pickup.address || (pickup.dateTime && dayjs(pickup.dateTime).isValid())) ? (
                                  <Typography key={pi} sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                    <strong>Return Pickup{(product.returnPickups?.length || 0) > 1 ? ` ${pi + 1}` : ""}:</strong>{" "}
                                    {pickup.address}{pickup.city ? `, ${pickup.city}` : ""}
                                    {pickup.dateTime && dayjs(pickup.dateTime).isValid() && <> · {dayjs(pickup.dateTime).format("DD-MMM-YY hh:mm A")}</>}
                                  </Typography>
                                ) : null
                              ))}
                              {(product.returnDropoffs || []).map((drop, di) => (
                                (drop.address || (drop.dateTime && dayjs(drop.dateTime).isValid())) ? (
                                  <Typography key={di} sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                    <strong>Return Dropoff{(product.returnDropoffs?.length || 0) > 1 ? ` ${di + 1}` : ""}:</strong>{" "}
                                    {drop.address}{drop.city ? `, ${drop.city}` : ""}
                                    {drop.dateTime && dayjs(drop.dateTime).isValid() && <> · {dayjs(drop.dateTime).format("DD-MMM-YY hh:mm A")}</>}
                                  </Typography>
                                ) : null
                              ))}
                              {/* Fallback: old-format flat fields (for records created before array format) */}
                              {!(product.pickups?.length) && product.departureAddress && (
                                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                  <strong>Departure:</strong> {product.departureAddress}
                                  {product.departureDateTime && dayjs(product.departureDateTime).isValid() && <> · {dayjs(product.departureDateTime).format("DD-MMM-YY hh:mm A")}</>}
                                </Typography>
                              )}
                              {!(product.dropoffs?.length) && product.destinationAddress && (
                                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                                  <strong>Destination:</strong> {product.destinationAddress}
                                  {product.returnDateTime && dayjs(product.returnDateTime).isValid() && <> · {dayjs(product.returnDateTime).format("DD-MMM-YY hh:mm A")}</>}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top", borderBottom: "1px solid #f1f5f9", py: 2 }}>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
                                ${product.total != null ? product.total.toFixed(2) : "0.00"}
                              </Typography>
                              {product.price != null && product.busCount != null && product.busCount > 1 && (
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                                  ${product.price.toFixed(2)} × {product.busCount} buses
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: "top", borderBottom: "1px solid #f1f5f9", py: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                                <IconButton size="small" color="primary" onClick={() => handleEditRow(index)} sx={{ "&:hover": { bgcolor: "#eff6ff" } }}>
                                  <FaEdit style={{ fontSize: 15 }} />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteRow(index)} sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                                  <FaTrash style={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>

            {/* ── Trip Dialog ── */}
          <Dialog open={popupOpen} onClose={() => setPopupOpen(false)} maxWidth="lg" fullWidth
            PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden", maxHeight: "92vh" } }}>

            {/* Dialog Header */}
            <Box sx={{ px: 3, py: 2, background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: 18 }}>🚌</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#ffffff", lineHeight: 1.2 }}>
                    {editIndex !== null ? "Edit Trip" : "Add Trip"}
                  </Typography>
                  {currentRow?.productName && (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>{currentRow.productName}</Typography>
                  )}
                </Box>
              </Box>
              <IconButton size="small" onClick={handleCancel} sx={{ color: "#94a3b8", "&:hover": { color: "#ffffff", bgcolor: "rgba(255,255,255,0.1)" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: "auto" }}>
              <Grid container sx={{ minHeight: 0 }}>
                   {/* ── Right Column: Route ── */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ p: 3 }}>

                    {/* Outbound Section Label */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 4, height: 20, bgcolor: "#2563eb", borderRadius: 2 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Outbound Route</Typography>
                      </Box>
                      {prevTrips.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Reuse a previous trip to save time</Typography>
                          <Button size="small" variant="contained"
                            onClick={() => setPrevTripDialogOpen(true)}
                            sx={{ fontSize: 11, fontWeight: 600, textTransform: "none", bgcolor: "#2563eb", borderRadius: 1.5, py: 0.5, px: 1.5, "&:hover": { bgcolor: "#1d4ed8" } }}>
                            Use Previous Trip
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Pickups */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>Pickup Locations</Typography>
                        {WorkOrderDataList?.customer && (WorkOrderDataList.customer.customerStreetNumber || WorkOrderDataList.customer.customerStreetName) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const updated = [...productRows];
                              const product = updated[rowIdx];
                              if (product.pickups && product.pickups.length > 0) {
                                const clientStreet = [WorkOrderDataList.customer.customerStreetNumber, WorkOrderDataList.customer.customerStreetName].filter(Boolean).join(" ");
                                const clientCity = WorkOrderDataList.customer.customerCity || "";
                                const clientName = WorkOrderDataList.customer.customerCompanyName || WorkOrderDataList.customer.customerFirstName || "";
                                product.pickups[0] = {
                                  ...product.pickups[0],
                                  description: clientName,
                                  address: clientStreet,
                                  city: clientCity,
                                };
                              }
                              setProductRows(updated);
                            }}
                            sx={{
                              fontSize: 11, fontWeight: 600, textTransform: "none",
                              borderColor: "#2f3891", color: "#2f3891", borderRadius: 1.5, py: 0.25, px: 1,
                              "&:hover": { bgcolor: "#ede9fe", borderColor: "#2f3891" },
                            }}
                          >
                            Use Client Address
                          </Button>
                        )}
                      </Box>
                      {WorkOrderDataList?.customer && (
                        <Box sx={{ mb: 1, px: 1.5, py: 0.75, bgcolor: "#f0f0ff", borderRadius: 1.5, border: "1px dashed #c7d2fe" }}>
                          <Typography sx={{ fontSize: 11, color: "#2f3891", fontWeight: 500 }}>
                            Client: {WorkOrderDataList.customer.customerCompanyName || WorkOrderDataList.customer.customerFirstName}
                            {(WorkOrderDataList.customer.customerStreetNumber || WorkOrderDataList.customer.customerStreetName) && (
                              <> — {[WorkOrderDataList.customer.customerStreetNumber, WorkOrderDataList.customer.customerStreetName].filter(Boolean).join(" ")}{WorkOrderDataList.customer.customerCity ? `, ${WorkOrderDataList.customer.customerCity}` : ""}</>
                            )}
                          </Typography>
                        </Box>
                      )}
                      {(currentRow?.pickups || []).map((pickup, pickupIndex) => (
                        <Box key={pickupIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                          {/* Step indicator */}
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 0.5, minWidth: 20 }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#ffffff" }}>{pickupIndex + 1}</Typography>
                            </Box>
                          </Box>
                          {/* Fields */}
                          <Box sx={{ flex: 1, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5 }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <Autocomplete freeSolo options={savedDescriptions} value={pickup.description || ""}
                                  onInputChange={(_, val) => handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "description", val)}
                                  onChange={(_, newVal, reason) => {
                                    if (reason === "selectOption" && typeof newVal === "string") {
                                      const loc = savedLocations.find(l => l.description === newVal);
                                      if (loc) {
                                        handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "address", loc.address);
                                        handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "city", matchCity(loc.city));
                                      }
                                    }
                                  }}
                                  renderInput={(params) => <TextField {...params} placeholder="Description" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <AddressAutocomplete
                                  value={pickup.address}
                                  savedLocations={savedLocations}
                                  onChange={(val) => handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "address", val)}
                                  onSelect={(addr, city, desc) => {
                                    handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "address", addr);
                                    handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "city", matchCity(city));
                                    if (desc) handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "description", desc);
                                    persistLocation(pickup.description || "", addr, city);
                                    refreshSavedLocations();
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Autocomplete options={canadianCities} value={pickup.city || ""}
                                  filterOptions={cityFilterOptions}
                                  onChange={(event, newValue) => handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "city", newValue ?? "")}
                                  renderInput={(params) => <TextField {...params} placeholder="City" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <DateTimeDropdownPicker
                                  value={pickup.dateTime}
                                  onChange={(val) => handleFieldChangeArray(rowIdx, "pickups", pickupIndex, "dateTime", val)}
                                  inputSx={{ bgcolor: "#ffffff" }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                          {(currentRow?.pickups?.length || 0) > 1 && (
                            <IconButton color="error" size="small" onClick={() => removeArrayItem(rowIdx, "pickups", pickupIndex)} sx={{ mt: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <Button size="small" variant="text" onClick={() => addArrayItem(rowIdx, "pickups")}
                        sx={{ color: "#2563eb", fontSize: 12, fontWeight: 600, pl: 3.5 }}>
                        + Add Pickup Stop
                      </Button>
                    </Box>

                    {/* Dropoffs */}
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>Dropoff Locations</Typography>
                      {(currentRow?.dropoffs || []).map((drop, dropIndex) => (
                        <Box key={dropIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 0.5, minWidth: 20 }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#ffffff" }}>{dropIndex + 1}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5 }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <Autocomplete freeSolo options={savedDescriptions} value={drop.description || ""}
                                  onInputChange={(_, val) => handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "description", val)}
                                  onChange={(_, newVal, reason) => {
                                    if (reason === "selectOption" && typeof newVal === "string") {
                                      const loc = savedLocations.find(l => l.description === newVal);
                                      if (loc) {
                                        handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "address", loc.address);
                                        handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "city", matchCity(loc.city));
                                      }
                                    }
                                  }}
                                  renderInput={(params) => <TextField {...params} placeholder="Description" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <AddressAutocomplete
                                  value={drop.address}
                                  savedLocations={savedLocations}
                                  onChange={(val) => handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "address", val)}
                                  onSelect={(addr, city, desc) => {
                                    handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "address", addr);
                                    handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "city", matchCity(city));
                                    if (desc) handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "description", desc);
                                    persistLocation(drop.description || "", addr, city);
                                    refreshSavedLocations();
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Autocomplete options={canadianCities} value={drop.city || ""}
                                  filterOptions={cityFilterOptions}
                                  onChange={(event, newValue) => handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "city", newValue ?? "")}
                                  renderInput={(params) => <TextField {...params} placeholder="City" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <DateTimeDropdownPicker
                                  value={drop.dateTime}
                                  minDateTime={
                                    dropIndex === 0
                                      ? dropoffMinDate
                                      : (currentRow?.dropoffs?.[dropIndex - 1]?.dateTime && dayjs(currentRow.dropoffs[dropIndex - 1].dateTime as any).isValid()
                                          ? dayjs(currentRow.dropoffs[dropIndex - 1].dateTime as any)
                                          : dropoffMinDate)
                                  }
                                  onChange={(val) => handleFieldChangeArray(rowIdx, "dropoffs", dropIndex, "dateTime", val)}
                                  inputSx={{ bgcolor: "#ffffff" }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                          {(currentRow?.dropoffs?.length || 0) > 1 && (
                            <IconButton color="error" size="small" onClick={() => removeArrayItem(rowIdx, "dropoffs", dropIndex)} sx={{ mt: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <Button size="small" variant="text" onClick={() => addArrayItem(rowIdx, "dropoffs")}
                        sx={{ color: "#16a34a", fontSize: 12, fontWeight: 600, pl: 3.5 }}>
                        + Add Dropoff Stop
                      </Button>
                    </Box>

                    {/* Return Route Section */}
                    {currentRow?.transferType !== TransferType.OneWayTransfer && (
                      <>
                        <Box sx={{ borderTop: "1px dashed #e2e8f0", pt: 2.5, mb: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box sx={{ width: 4, height: 20, bgcolor: "#f59e0b", borderRadius: 2 }} />
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Return Route</Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={currentRow?.useSameAsOutbound || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const updated = [...productRows];
                                    const product = updated[rowIdx];
                                    product.useSameAsOutbound = e.target.checked;
                                    if (e.target.checked) {
                                      product.returnPickups = product.dropoffs?.map(d => ({ address: d.address, city: d.city, dateTime: null, description: d.description })) || [];
                                      product.returnDropoffs = product.pickups?.slice().reverse().map(p => ({ address: p.address, city: p.city, dateTime: null, description: p.description })) || [];
                                    } else {
                                      product.returnPickups = [{ address: "", city: "", dateTime: null }];
                                      product.returnDropoffs = [{ address: "", city: "", dateTime: null }];
                                    }
                                    setProductRows(updated);
                                  }}
                                  sx={{ color: "#f59e0b", "&.Mui-checked": { color: "#f59e0b" } }}
                                />
                              }
                              label={<Typography sx={{ fontSize: 12, color: "#64748b" }}>Mirror outbound (reversed)</Typography>}
                            />
                          </Box>
                        </Box>

                        {/* Return Pickups */}
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>Return Pickup Locations</Typography>
                          {(currentRow?.returnPickups || []).map((pickup, pickupIndex) => (
                            <Box key={pickupIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                              <Box sx={{ mt: 0.5, minWidth: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#ffffff" }}>{pickupIndex + 1}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ flex: 1, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1.5, p: 1.5 }}>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item xs={12} sm={3}>
                                    <Autocomplete freeSolo options={savedDescriptions} value={pickup.description || ""}
                                      onInputChange={(_, val) => handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "description", val)}
                                      onChange={(_, newVal, reason) => {
                                        if (reason === "selectOption" && typeof newVal === "string") {
                                          const loc = savedLocations.find(l => l.description === newVal);
                                          if (loc) {
                                            handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "address", loc.address);
                                            handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "city", matchCity(loc.city));
                                          }
                                        }
                                      }}
                                      renderInput={(params) => <TextField {...params} placeholder="Description" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <AddressAutocomplete
                                      value={pickup.address}
                                      savedLocations={savedLocations}
                                      onChange={(val) => handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "address", val)}
                                      onSelect={(addr, city, desc) => {
                                        handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "address", addr);
                                        handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "city", matchCity(city));
                                        if (desc) handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "description", desc);
                                        persistLocation(pickup.description || "", addr, city);
                                        refreshSavedLocations();
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Autocomplete options={canadianCities} value={pickup.city || ""}
                                      filterOptions={cityFilterOptions}
                                      onChange={(event, newValue) => handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "city", newValue ?? "")}
                                      renderInput={(params) => <TextField {...params} placeholder="City" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <DateTimeDropdownPicker
                                      value={pickup.dateTime}
                                      minDateTime={
                                        pickupIndex === 0
                                          ? returnPickupMinDate
                                          : (currentRow?.returnPickups?.[pickupIndex - 1]?.dateTime && dayjs(currentRow.returnPickups[pickupIndex - 1].dateTime as any).isValid()
                                              ? dayjs(currentRow.returnPickups[pickupIndex - 1].dateTime as any)
                                              : returnPickupMinDate)
                                      }
                                      onChange={(val) => handleFieldChangeArray(rowIdx, "returnPickups", pickupIndex, "dateTime", val)}
                                      inputSx={{ bgcolor: "#ffffff" }}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                              {(currentRow?.returnPickups?.length || 0) > 1 && (
                                <IconButton color="error" size="small" onClick={() => removeArrayItem(rowIdx, "returnPickups", pickupIndex)} sx={{ mt: 0.5 }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          <Button size="small" variant="text" onClick={() => addArrayItem(rowIdx, "returnPickups")}
                            sx={{ color: "#f59e0b", fontSize: 12, fontWeight: 600, pl: 3.5 }}>
                            + Add Return Pickup
                          </Button>
                        </Box>

                        {/* Return Dropoffs */}
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>Return Dropoff Locations</Typography>
                          {(currentRow?.returnDropoffs || []).map((drop, dropIndex) => (
                            <Box key={dropIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                              <Box sx={{ mt: 0.5, minWidth: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#ffffff" }}>{dropIndex + 1}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ flex: 1, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1.5, p: 1.5 }}>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item xs={12} sm={3}>
                                    <Autocomplete freeSolo options={savedDescriptions} value={drop.description || ""}
                                      onInputChange={(_, val) => handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "description", val)}
                                      onChange={(_, newVal, reason) => {
                                        if (reason === "selectOption" && typeof newVal === "string") {
                                          const loc = savedLocations.find(l => l.description === newVal);
                                          if (loc) {
                                            handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "address", loc.address);
                                            handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "city", matchCity(loc.city));
                                          }
                                        }
                                      }}
                                      renderInput={(params) => <TextField {...params} placeholder="Description" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <AddressAutocomplete
                                      value={drop.address}
                                      savedLocations={savedLocations}
                                      onChange={(val) => handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "address", val)}
                                      onSelect={(addr, city, desc) => {
                                        handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "address", addr);
                                        handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "city", matchCity(city));
                                        if (desc) handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "description", desc);
                                        persistLocation(drop.description || "", addr, city);
                                        refreshSavedLocations();
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Autocomplete options={canadianCities} value={drop.city || ""}
                                      filterOptions={cityFilterOptions}
                                      onChange={(event, newValue) => handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "city", newValue ?? "")}
                                      renderInput={(params) => <TextField {...params} placeholder="City" size="small" InputProps={{ ...params.InputProps, sx: { bgcolor: "#ffffff" } }} />} />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <DateTimeDropdownPicker
                                      value={drop.dateTime}
                                      minDateTime={
                                        dropIndex === 0
                                          ? returnDropoffMinDate
                                          : (currentRow?.returnDropoffs?.[dropIndex - 1]?.dateTime && dayjs(currentRow.returnDropoffs[dropIndex - 1].dateTime as any).isValid()
                                              ? dayjs(currentRow.returnDropoffs[dropIndex - 1].dateTime as any)
                                              : returnDropoffMinDate)
                                      }
                                      onChange={(val) => handleFieldChangeArray(rowIdx, "returnDropoffs", dropIndex, "dateTime", val)}
                                      inputSx={{ bgcolor: "#ffffff" }}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                              {(currentRow?.returnDropoffs?.length || 0) > 1 && (
                                <IconButton color="error" size="small" onClick={() => removeArrayItem(rowIdx, "returnDropoffs", dropIndex)} sx={{ mt: 0.5 }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          <Button size="small" variant="text" onClick={() => addArrayItem(rowIdx, "returnDropoffs")}
                            sx={{ color: "#f59e0b", fontSize: 12, fontWeight: 600, pl: 3.5 }}>
                            + Add Return Dropoff
                          </Button>
                        </Box>
                      </>
                    )}

                  </Box>
                </Grid>
                {/* ── Left Column: Bus Info + Pricing ── */}
                <Grid item xs={12} md={4} sx={{ borderRight: { md: "1px solid #e2e8f0" }, bgcolor: "#f8fafc" }}>
                  <Box sx={{ p: 3 }}>

                    {/* Bus / Product */}
                    <Box sx={{ mb: 3, position: "relative" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Bus / Service</Typography>
                      <TextField
                        size="small" fullWidth
                        value={currentRow?.productName || ""}
                        onChange={(e) => { handleProductNameChange(rowIdx, e.target.value); if (errors.productName) setErrors((p) => ({ ...p, productName: undefined })); }}
                        placeholder="Search bus or service..."
                        error={!!errors.productName}
                        helperText={errors.productName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CiSearch style={{ fontSize: 16, color: "#94a3b8" }} />
                            </InputAdornment>
                          ),
                          sx: { bgcolor: "#ffffff" },
                        }}
                        onFocus={() => setShowDropdownProductIndex(rowIdx)}
                      />
                      {showDropdownProductIndex === rowIdx && (
                        <Paper sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, mt: 0.5, maxHeight: 200, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                          {productIsLoading ? (
                            <Box sx={{ textAlign: "center", p: 2 }}><CircularProgress size={20} /></Box>
                          ) : (
                            productList.map((Product) => (
                              <MenuItem key={Product.id} onClick={() => handleProductSelect(Product, rowIdx)}
                                sx={{ gap: 1.5, py: 1, "&:hover": { bgcolor: "#eff6ff" } }}>
                                <img
                                  src={Product.productImg ? `${LARAVEL_API_URL}/${String(Product.productImg)}` : placeholderImage}
                                  alt="img"
                                  style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6 }}
                                />
                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{Product.productName}</Typography>
                              </MenuItem>
                            ))
                          )}
                        </Paper>
                      )}
                    </Box>

                    {/* Passengers + Transfer Type */}
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Trip Details</Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography sx={{ ...infoLabel, mb: 0.5 }}>Passengers</Typography>
                          <TextField size="small" fullWidth type="number" placeholder="0"
                            value={currentRow?.numberOfPassengers || ""}
                            onChange={(e) => { handleFieldChange(rowIdx, "numberOfPassengers", Number(e.target.value)); if (errors.numberOfPassengers) setErrors((p) => ({ ...p, numberOfPassengers: undefined })); }}
                            error={!!errors.numberOfPassengers}
                            helperText={errors.numberOfPassengers}
                            InputProps={{ inputProps: { min: 0 }, sx: { bgcolor: "#ffffff" } }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ ...infoLabel, mb: 0.5 }}>Buses (Qty)</Typography>
                          <TextField size="small" fullWidth type="number" placeholder="1"
                            value={currentRow?.busCount || ""}
                            onChange={(e) => { handleFieldChange(rowIdx, "busCount", Number(e.target.value)); if (errors.busCount) setErrors((p) => ({ ...p, busCount: undefined })); }}
                            error={!!errors.busCount}
                            helperText={errors.busCount}
                            InputProps={{ inputProps: { min: 0 }, sx: { bgcolor: "#ffffff" } }} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography sx={{ ...infoLabel, mb: 0.5 }}>Transfer Type</Typography>
                          <TextField select size="small" fullWidth
                            value={currentRow?.transferType ?? ""}
                            onChange={(e) => { handleFieldChange(rowIdx, "transferType", String(e.target.value)); if (errors.transferType) setErrors((p) => ({ ...p, transferType: undefined })); }}
                            error={!!errors.transferType}
                            helperText={errors.transferType}
                            InputProps={{ sx: { bgcolor: "#ffffff" } }}>
                            <MenuItem value="" disabled>Select type</MenuItem>
                            {Object.entries(TransferType).map(([key, value], i) => (
                              <MenuItem key={i} value={value}>{key}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Pricing */}
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Pricing</Typography>

                      {/* Price history */}
                      {priceHistoryIsLoading && (
                        <Box sx={{ textAlign: "center", py: 1 }}><CircularProgress size={18} /></Box>
                      )}
                      {nextPrice && (
                        <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>Price History</Typography>
                          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Box>
                              <Typography sx={{ fontSize: 10, color: "#64748b" }}>Last Price</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{nextPrice}</Typography>
                              <Typography sx={{ fontSize: 11, color: "#64748b" }}>{busCount} bus{busCount !== 1 ? "es" : ""}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 10, color: "#64748b" }}>Average</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{averagePrice}</Typography>
                              {updated_at && <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>{new Date(updated_at).toLocaleDateString()}</Typography>}
                            </Box>
                          </Box>
                          {quoteWorkOrderNoForPrice && (
                            <Typography sx={{ fontSize: 11, color: "#2563eb", fontWeight: 600, cursor: "pointer", textDecoration: "underline", mt: 0.5 }}
                              onClick={() => window.open(`/work-order/history/${priceId}`, "_blank")}>
                              View: {quoteWorkOrderNoForPrice}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {priceHistoryError && currentIndex !== null && (
                        <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: 12 }}>
                          Error loading price history for {productRows[currentIndex]?.productName}.
                        </Alert>
                      )}

                      {/* Route-based Price Suggestion Card */}
                      <PriceSuggestionCard
                        pickupCity={currentRow?.pickups?.[0]?.city || ""}
                        dropoffCity={currentRow?.dropoffs?.[0]?.city || ""}
                        busCount={currentRow?.busCount ?? 1}
                        onApplyPrice={(price) => handlePriceChange(rowIdx, price)}
                      />

                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <Typography sx={{ ...infoLabel, mb: 0.5 }}>Trip Price ($)</Typography>
                          {(() => {
                            const buses = Math.max(1, Number(currentRow?.busCount || 1));
                            const perBus = Number(currentRow?.price || 0);
                            const total  = perBus * buses;
                            return (
                              <>
                                <TextField size="small" fullWidth type="number" placeholder="0.00"
                                  value={total ? total : ""}
                                  onChange={(e) => {
                                    const newTotal = Math.max(0, Number(e.target.value));
                                    const newPerBus = buses > 0 ? newTotal / buses : newTotal;
                                    handlePriceChange(rowIdx, newPerBus);
                                    if (errors.price) setErrors((p) => ({ ...p, price: undefined }));
                                  }}
                                  error={!!errors.price}
                                  helperText={errors.price || (buses > 1 ? `$${perBus.toFixed(2)}/bus × ${buses} buses` : undefined)}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { bgcolor: "#ffffff" } }} />
                              </>
                            );
                          })()}
                        </Grid>
                        <Grid item xs={12}>
                          <Typography sx={{ ...infoLabel, mb: 0.5 }}>Tax</Typography>
                          <TextField select size="small" fullWidth
                            value={currentRow?.tax || 0}
                            onChange={(e) => {
                              const taxPercentage = Number(e.target.value);
                              const productPrice = currentRow?.price || 0;
                              const taxAmount = productPrice * (taxPercentage / 100);
                              handleFieldChange(rowIdx, "tax", taxPercentage);
                              handleFieldChange(rowIdx, "taxAmount", taxAmount);
                            }}
                            InputProps={{ sx: { bgcolor: "#ffffff" } }}>
                            <MenuItem value={0}>No Tax</MenuItem>
                            {taxList && taxList.map((tax) => (
                              <MenuItem key={tax.id} value={tax.percentage}>{tax.taxName} – {tax.percentage}%</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        {(currentRow?.tax || 0) > 0 && (
                          <Grid item xs={12}>
                            <Box sx={{ p: 1.5, bgcolor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: 12, color: "#64748b" }}>Tax Amount</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>${currentRow?.taxAmount?.toFixed(2) || "0.00"}</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {/* Description */}
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Description</Typography>
                      <TextField size="small" fullWidth multiline rows={4} placeholder="Additional notes about this trip..."
                        value={currentRow?.productDescription || ""}
                        onChange={(e) => handleFieldChange(rowIdx, "productDescription", e.target.value)}
                        InputProps={{ sx: { bgcolor: "#ffffff" } }} />
                    </Box>
                  </Box>
                </Grid>

               
              </Grid>
            </DialogContent>

            {/* Dialog Footer */}
            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancel}
                sx={{ borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" } }}>
                Cancel
              </Button>
              <Button variant="contained" startIcon={<SaveAltIcon />} onClick={handleSaveProduct}
                sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" }, px: 3 }}>
                {editIndex !== null ? "Update Trip" : "Save Trip"}
              </Button>
            </Box>
          </Dialog>
          {/* ── Section 4: Discounts & Summary ── */}
          <Box sx={sectionCard}>
            <Box sx={sectionHeader}>
              <Typography sx={sectionTitle}>Discounts & Summary</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog(null)} sx={{ borderColor: "#2563eb", color: "#2563eb", fontSize: 13 }}>Add Discount</Button>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={4}>
                {/* Discounts list */}
                <Grid item lg={8} md={8} sm={12} xs={12}>
                  {discounts.length === 0 ? (
                    <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>No discounts applied.</Typography>
                  ) : (
                    discounts.map((discount, index) => (
                      <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, p: 1.5, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>{discount.description || "Discount"}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#dc2626" }}>
                          {`-$${(discount.value ?? 0).toFixed(2)}`}
                        </Typography>
                        <IconButton size="small" onClick={() => handleOpenDialog(index)} sx={{ color: "#2563eb" }}><FaEdit style={{ fontSize: 13 }} /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteDiscount(index)} sx={{ color: "#dc2626" }}><FaTrash style={{ fontSize: 12 }} /></IconButton>
                      </Box>
                    ))
                  )}
                </Grid>
                {/* Summary */}
                <Grid item lg={4} md={4} sm={12} xs={12}>
                  <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                    {[
                      { label: "Subtotal", value: `$${subTotal.toFixed(2)}`, color: "#1e293b" },
                      { label: "Total Discount", value: `-$${totalDiscount.toFixed(2)}`, color: "#dc2626" },
                      { label: "HST", value: `$${taxAmount.toFixed(2)}`, color: "#1e293b" },
                    ].map((row) => (
                      <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: row.color }}>{row.value}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1.5, bgcolor: "#1e293b" }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#ffffff" }}>Grand Total</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#ffffff" }}>
                        ${isNaN(grandTotalEithDiscount) ? "0.00" : (grandTotalEithDiscount ?? 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* ── Section 5: Remarks ── */}
          <Box sx={sectionCard}>
            <Box sx={sectionHeader}>
              <Typography sx={sectionTitle}>Remarks</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ ...infoLabel, mb: 0.5 }}>Special Notification</Typography>
                  <TextField
                    placeholder="Enter special notification for customer (visible on invoice/PDF/email)..."
                    size="small" fullWidth multiline rows={6}
                    {...formik.getFieldProps("quoteWorkOrderCustomerRemark")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ ...infoLabel, mb: 0.5 }}>Internal / Office Notes</Typography>
                  <TextField
                    placeholder="Enter internal notes (admin-only, not on invoice/PDF/email)..."
                    size="small" fullWidth multiline rows={6}
                    {...formik.getFieldProps("quoteWorkOrderCustomerMemo")}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* ── Bottom Action Bar ── */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pb: 2 }}>
            <Button variant="outlined" startIcon={<ArrowBackIosIcon />} onClick={() => navigate(-1)} sx={{ borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}>Back</Button>
            <Button variant="contained" startIcon={<SaveAltIcon />} onClick={() => formik.handleSubmit()} disabled={isLoading} sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}>Update</Button>
          </Box>


          {/* ── Discount Dialog ── */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}>

            {/* Header */}
            <Box sx={{ px: 3, py: 2, background: "linear-gradient(135deg, #2f3891 0%, #252e7a 100%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: 18, lineHeight: 1 }}>🏷️</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#ffffff", lineHeight: 1.2 }}>
                    {editingIndex !== null ? "Edit Discount" : "Add Discount"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)", mt: 0.25 }}>
                    Subtotal: ${subTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={handleCloseDialog}
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff", bgcolor: "rgba(255,255,255,0.12)" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 3 }}>

              {/* Type Toggle + Amount Input */}
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={quoteInfoLabel}>Discount Amount</Typography>
                  <Box sx={{ display: "flex", border: "1.5px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
                    {(["fixed", "percent"] as const).map((t) => (
                      <Box
                        key={t}
                        onClick={() => { setDiscountType(t); setDiscountValue(0); }}
                        sx={{
                          px: 1.5, py: 0.4, fontSize: 13, fontWeight: 700,
                          cursor: "pointer", userSelect: "none", transition: "all 0.15s",
                          bgcolor: discountType === t ? "#2f3891" : "#f8fafc",
                          color: discountType === t ? "#ffffff" : "#64748b",
                          "&:hover": discountType !== t ? { bgcolor: "#f5f3ff", color: "#2f3891" } : {},
                        }}
                      >
                        {t === "fixed" ? "$" : "%"}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <TextField
                  fullWidth type="number" placeholder="0"
                  value={discountValue || ""}
                  onChange={(e) => {
                    const v = Math.max(0, Number(e.target.value));
                    setDiscountValue(discountType === "percent" ? Math.min(100, v) : v);
                  }}
                  inputProps={{ min: 0, step: discountType === "percent" ? 1 : 0.01, max: discountType === "percent" ? 100 : undefined }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#2f3891", lineHeight: 1 }}>
                          {discountType === "fixed" ? "$" : "%"}
                        </Typography>
                      </InputAdornment>
                    ),
                    sx: { fontSize: 28, fontWeight: 700, color: "#1e293b", "& input": { py: 1.5 } },
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891", borderWidth: 2 } } }}
                />
                {discountType === "percent" && discountValue > 0 && subTotal > 0 && (
                  <Typography sx={{ fontSize: 12, color: "#2f3891", mt: 0.5, fontWeight: 600 }}>
                    = ${((discountValue / 100) * subTotal).toFixed(2)} off
                  </Typography>
                )}
              </Box>

              {/* Quick Presets */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...quoteInfoLabel, mb: 1 }}>Quick {discountType === "fixed" ? "Amounts" : "Percentages"}</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(discountType === "fixed" ? [25, 50, 100, 150, 200, 500] : [5, 10, 15, 20, 25, 50]).map((preset) => (
                    <Box
                      key={preset}
                      onClick={() => setDiscountValue(preset)}
                      sx={{
                        px: 1.5, py: 0.6, borderRadius: 1.5,
                        border: "1.5px solid",
                        borderColor: discountValue === preset ? "#2f3891" : "#e2e8f0",
                        bgcolor: discountValue === preset ? "#f5f3ff" : "#f8fafc",
                        color: discountValue === preset ? "#2f3891" : "#64748b",
                        fontWeight: discountValue === preset ? 700 : 500,
                        fontSize: 13, cursor: "pointer", userSelect: "none", transition: "all 0.15s",
                        "&:hover": { borderColor: "#2f3891", bgcolor: "#f5f3ff", color: "#2f3891" },
                      }}
                    >
                      {discountType === "fixed" ? `$${preset}` : `${preset}%`}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...quoteInfoLabel, mb: 1 }}>Description (optional)</Typography>
                <TextField
                  fullWidth multiline rows={3}
                  placeholder="e.g. Loyalty discount, group rate, promo code..."
                  value={discountDescription}
                  onChange={(e) => setDiscountDescription(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" } } }}
                />
              </Box>

              {/* Live Preview */}
              {discountValue > 0 && (() => {
                const thisDiscountDollar = discountType === "percent" ? (discountValue / 100) * subTotal : discountValue;
                const otherDiscountsDollar = discounts.reduce((a, d, i) => {
                  if (i === editingIndex) return a;
                  return a + (d.type === "percent" ? ((d.value ?? 0) / 100) * subTotal : (d.value ?? 0));
                }, 0);
                const newTotal = Math.max(0, subTotal - thisDiscountDollar - otherDiscountsDollar + taxAmount);
                return (
                  <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <Box sx={{ px: 2, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>Preview</Typography>
                    </Box>
                    {[
                      { label: "Subtotal", value: `$${subTotal.toFixed(2)}`, color: "#1e293b", bold: false },
                      { label: discountType === "percent" ? `This discount (${discountValue}%)` : "This discount", value: `-$${thisDiscountDollar.toFixed(2)}`, color: "#dc2626", bold: true },
                      { label: "Other discounts", value: `-$${otherDiscountsDollar.toFixed(2)}`, color: "#64748b", bold: false },
                      { label: "Tax (HST)", value: `$${taxAmount.toFixed(2)}`, color: "#64748b", bold: false },
                    ].map((row) => (
                      <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 0.8, borderBottom: "1px solid #f1f5f9" }}>
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: row.bold ? 700 : 500, color: row.color }}>{row.value}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1.25, bgcolor: "#1e293b" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ffffff" }}>New Grand Total</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#ffffff" }}>${newTotal.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                );
              })()}
            </DialogContent>

            {/* Footer */}
            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc", display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={handleCloseDialog}
                sx={{ borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" } }}>
                Cancel
              </Button>
              <Button variant="contained" startIcon={<SaveAltIcon />} onClick={handleSaveDiscount}
                disabled={discountValue <= 0}
                sx={{ bgcolor: "#2f3891", "&:hover": { bgcolor: "#252e7a" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                {editingIndex !== null ? "Update" : "Add Discount"}
              </Button>
            </Box>
          </Dialog>

          {openCompany && (
            <NewCompanyPopUp
              handleCloseDialog={() => setOpenCompany(false)}
              openModel={openCompany}
            />
          )}
        </Box>

      </form>

      {/* ── Driver-already-assigned confirmation ── */}
      <Dialog
        open={driverConfirmOpen}
        onClose={() => setDriverConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
      >
        <Box sx={{ px: 3, py: 2, bgcolor: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#b45309" }}>
            Driver Already Assigned
          </Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 13, color: "#1e293b", lineHeight: 1.5 }}>
            Changing pickup/drop-off time may affect the assigned driver's schedule. Do you want to continue?
          </Typography>
        </DialogContent>
        <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDriverConfirmOpen(false);
              setPendingSubmitValues(null);
              setPendingSubmitHelpers(null);
            }}
            sx={{ borderColor: "#e2e8f0", color: "#64748b", textTransform: "none", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const v = pendingSubmitValues;
              const h = pendingSubmitHelpers;
              setDriverConfirmOpen(false);
              setPendingSubmitValues(null);
              setPendingSubmitHelpers(null);
              if (v && h) await runSubmit(v, h);
            }}
            sx={{ bgcolor: "#2563eb", textTransform: "none", "&:hover": { bgcolor: "#1d4ed8" } }}
          >
            Continue
          </Button>
        </Box>
      </Dialog>

      {/* ── Previous Trip Selection Dialog ── */}
      <Dialog open={prevTripDialogOpen} onClose={() => setPrevTripDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2, background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#ffffff" }}>Select Previous Trip</Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>Choose a past trip to auto-fill the form</Typography>
          </Box>
          <IconButton size="small" onClick={() => setPrevTripDialogOpen(false)} sx={{ color: "#94a3b8", "&:hover": { color: "#ffffff", bgcolor: "rgba(255,255,255,0.1)" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {prevTrips.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No previous trips found for this client.</Typography>
            </Box>
          ) : (
            <Box>
              {prevTrips.map((trip, i) => {
                const pickupCity  = trip.pickups?.[0]?.city  || trip.transferTypeFrom || "—";
                const dropoffCity = trip.dropoffs?.[0]?.city || trip.transferTypeTo   || "—";
                const tripDate    = trip.workOrderDate ? dayjs(trip.workOrderDate).format("MMM D, YYYY") : "—";
                return (
                  <Box key={i}
                    onClick={() => handleApplyPreviousTrip(trip)}
                    sx={{
                      px: 3, py: 2, cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      "&:hover": { bgcolor: "#eff6ff" },
                      "&:last-child": { borderBottom: "none" },
                    }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                          {pickupCity} → {dropoffCity}
                        </Typography>
                        <Box sx={{ px: 0.75, py: 0.2, bgcolor: "#eff6ff", borderRadius: 1, border: "1px solid #bfdbfe" }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#2563eb" }}>{trip.workOrderNo}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>{tripDate}</Typography>
                        {trip.productName && <Typography sx={{ fontSize: 11, color: "#64748b" }}>• {trip.productName}</Typography>}
                        {trip.numberOfPassengers && <Typography sx={{ fontSize: 11, color: "#64748b" }}>• {trip.numberOfPassengers} pax</Typography>}
                        {trip.busCount && trip.busCount > 1 && <Typography sx={{ fontSize: 11, color: "#64748b" }}>• {trip.busCount} buses</Typography>}
                      </Box>
                    </Box>
                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Index;
